/**
 * Task 30: Story Reader
 * Story page viewer with text, images, and audio playback with page flip animations
 */

import { t } from '../i18n/config.js';
import { recDbg } from '../utils/recordingDebug.js';
import { loadStoryContent, trackProgress, awardStars } from '../services/content.js';
import { startLoadingAnimation, stopLoadingAnimation } from '../utils/loadingAnimation.js';
import {
  initializePageFlip,
  createPageElements,
  nextPage,
  prevPage,
  getPageCount,
  PREPENDED_PAGE_COUNT,
} from '../utils/pageFlip.js';
import { detectDeviceAndOrientation } from '../utils/deviceDetection.js';
import { getPageFlipMode } from '../config/deviceBreakpoints.js';
import { fetchStoryRecordings, uploadRecording, getFamilyId } from '../services/recordingService.js';
import { savePendingRecording, deletePendingRecording } from '../services/recordingStore.js';
import { showCoach, hideCoach, destroyCoach } from '../utils/recordingCoach.js';
import { showToast, appendToToast, dismissToast } from '../utils/toast.js';
import { router } from '../utils/router.js';
import { preloadStarImages, playStarReward, resetStarPreload } from '../utils/starReward.js';
import { ensureAudioUnlocked } from '../utils/audioUnlock.js';

// DEBUG FLAG: Set to true to show audio controls even when no audio is present
const DEBUG_AUDIO_CONTROLS = true;

// Global page flip instance
let pageFlipInstance = null;
let currentStoryData = null;
let currentProfile = null;
let currentModule = null;
let starRewardPlayed = false; // Prevent star animation from playing twice per module
let audioElement = null;
let isAudioPlaying = false;
let currentReadingMode = null;
let currentAdultRecordingIndex = null;
let currentAdultRecordingName = null;
let currentRecordingPageIndex = null; // Flipbook page index being recorded (includes cover+index offset)
let lastRecordedPageIndex = null; // Last page that was recorded (for advancing to next page on same spread)
let autoPlayTimer = null; // Timer for 2-second delay before auto-playing next page audio
let pageTurnClipAudio = null; // Cached Page_Turn_Clip Audio element
let coverAnimationTimer = null; // Timer ID for recursive cover centering animation
let coverCurrentOffset = 0; // Current translateX offset of #flipBook (px, negative = shifted left)
let fetchedAdultRecordings = []; // Adult narration recordings fetched from backend [{profileId, name, avatarUrl, pages: Map}]

/**
 * Page Turn Clip: short audio played at end of page audio to signal page turn.
 * Local bundled asset for offline support.
 */
const PAGE_TURN_CLIP_URL = '/audio/turn-page.mp3';

/**
 * Completion sequence timing.
 *
 * SEQUENCE_START_MS lets the celebration land before the first line appears.
 *
 * MIN_SEND_DWELL_MS keeps the "sending" line on screen long enough to read.
 * Pages upload as they are recorded, so by the time the narrator finishes there
 * is usually nothing left to flush and it returns immediately — without a floor
 * the sending and done lines would appear in the same frame.
 */
const SEQUENCE_START_MS = 500;
const MIN_SEND_DWELL_MS = 800;

/**
 * Pause reading session (called when hamburger menu opens)
 */
export function pauseReadingSession() {
  clearAutoPlayTimer();
  if (audioElement && isAudioPlaying) {
    audioElement.pause();
    console.log('⏸️ Reading session paused');
  }
}

/**
 * Resume reading session (called when hamburger menu closes)
 */
export function resumeReadingSession() {
  if (audioElement && isAudioPlaying) {
    audioElement.play();
    console.log('▶️ Reading session resumed');
  }
}

/**
 * Whether the flipbook must be locked to single-page (portrait) layout.
 *
 * Recording is page-by-page, so it needs a single page. Reading and listening
 * do not — an adult profile browsing the library should get the same layout as
 * a child on the same device.
 *
 * @returns {boolean}
 */
function shouldForcePortrait() {
  return !!(currentProfile?.isAdultRecorder || currentProfile?._forceRecordPortrait);
}

/**
 * Record the active page mode on the reader container so CSS can pick the right
 * spread ratio for .stf__wrapper.
 *
 * Needed because the desktop rule in reader.css constrains the wrapper to the
 * double-page ratio with !important, which StPageFlip then measures and uses to
 * decide portrait vs landscape.
 *
 * @param {'single'|'double'} mode
 * @returns {void}
 */
function setReaderPageMode(mode) {
  const container = document.querySelector('.story-reader-container');
  if (container) container.dataset.pageMode = mode;
}

/**
 * Reset the reader's module-level state.
 *
 * StoryReader keeps recordings and the selected audio mode in module scope, so
 * without this reset a profile switch (or opening a second story) inherits the
 * previous session's recordings, narrator selection and portrait override.
 */
function resetReaderState() {
  resetPageRecordings();
  fetchedAdultRecordings = [];
  currentReadingMode = null;
  currentAdultRecordingIndex = null;
  currentAdultRecordingName = null;
  currentRecordingPageIndex = null;
  lastRecordedPageIndex = null;
  sessionRecordCount = 0;
  previousFlipIndex = null;
  completionSequenceDone = false;
  clearTimeout(coachRestTimer);
  coachRestTimer = null;
  if (currentProfile) {
    delete currentProfile._forceRecordPortrait;
  }
}

/**
 * Cleanup story reader — stops silence detection, recording, audio.
 * Called when navigating away from the story reader.
 */
export function cleanupStoryReader() {
  stopSilenceDetection();
  clearAutoPlayTimer();
  if (audioElement) {
    audioElement.pause();
    audioElement = null;
  }
  isAudioPlaying = false;
  if (isRecording && mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
  isRecording = false;
  if (micStream) {
    micStream.getTracks().forEach((t) => t.stop());
    micStream = null;
  }
  // Remove any lingering modals
  const silenceModal = document.getElementById('silenceConfirmModal');
  if (silenceModal) silenceModal.remove();

  destroyCoach();

  // The completion toast has no timer of its own, so it must be cleared here or
  // it survives the view and hangs over whatever comes next.
  clearCompletionToast();

  // Drop per-story state so the next profile/story starts clean
  resetReaderState();
}

// Adult recording mode state
let adultRecordingParams = null; // { moduleId, familyId, displayName }
let sessionRecordCount = 0; // How many recordings made this session (drives countdown mode)

// The completion toast waits for the user instead of auto-dismissing, so it is
// held here and torn down explicitly — by clearCompletionToast() below.
let completionToast = null;
// Removes the listeners that dismiss the toast on outside interaction
let completionToastCleanup = null;
// Set once the session has been confirmed sent. Without it, swiping back to the
// end after dismissing the toast would replay the celebration and the sequence.
let completionSequenceDone = false;

/**
 * Remove the completion toast and stop watching for the interactions that
 * dismiss it.
 *
 * Safe to call when there is no toast, so teardown paths need no guards.
 *
 * @returns {void}
 */
function clearCompletionToast() {
  if (completionToastCleanup) {
    completionToastCleanup();
    completionToastCleanup = null;
  }
  if (completionToast) {
    dismissToast(completionToast);
    completionToast = null;
  }
}

/**
 * Dismiss the completion toast as soon as the narrator does anything else —
 * taps another button, or turns a page. Its own button is excluded so it still
 * works.
 *
 * Capture phase, so the toast goes even if the thing being tapped stops the
 * event from propagating.
 *
 * @returns {void}
 */
function watchForCompletionToastDismissal() {
  const onPointerDown = (e) => {
    if (completionToast && completionToast.contains(e.target)) return;
    clearCompletionToast();
  };

  document.addEventListener('pointerdown', onPointerDown, true);

  completionToastCleanup = () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
  };
}

/**
 * Promise-based delay, for sequencing the completion toast steps.
 * @param {number} ms
 * @returns {Promise<void>}
 */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Render adult recording mode — opened from admin app via deep link.
 * Skips the Audio Mode Menu, shows a status card with name input,
 * then enters recording mode on the story.
 *
 * Requirements: 9.5, 10.1, 10.2, 10.3, 10.4, 10.5
 *
 * @param {Object} params - { moduleId, familyId, displayName }
 */
export async function renderAdultRecordingMode(params) {
  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  adultRecordingParams = params;

  // Show status card UI
  contentContainer.innerHTML = `
    <div class="adult-recording-setup">
      <div class="adult-recording-card">
        <h2 class="adult-recording-title">${t('child.adultRecording.title') || "Neem 'n storie op vir jou kind / kleinkind."}</h2>
        <div class="adult-recording-field">
          <label for="adultDisplayName">${t('child.adultRecording.nameLabel') || 'Naam wat die kind gaan sien in hulle boekie:'}</label>
          <input type="text" id="adultDisplayName" class="adult-recording-input" value="${params.displayName}" />
        </div>
        <button class="adult-recording-start-btn" id="adultStartRecordingBtn">
          <span>${t('child.adultRecording.startRecording') || 'Begin Opneem'}</span>
        </button>
      </div>
    </div>
  `;

  // Bind start button
  document.getElementById('adultStartRecordingBtn')?.addEventListener('click', async () => {
    const nameInput = document.getElementById('adultDisplayName');
    const displayName = nameInput?.value?.trim() || params.displayName;
    adultRecordingParams.displayName = displayName;

    // Load the story and enter recording mode
    await startAdultRecording(params.moduleId, displayName);
  });
}

/**
 * Load story and start adult recording mode.
 * Creates a synthetic profile and module, then renders the story reader
 * in record mode (skipping the Audio Mode Menu).
 */
async function startAdultRecording(storyId, displayName) {
  const { loadStoryContent: loadContent } = await import('../services/content.js');
  const { startLoadingAnimation: startLoading, stopLoadingAnimation: stopLoading } =
    await import('../utils/loadingAnimation.js');

  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) return;

  contentContainer.innerHTML = '<div class="story-reader-container"></div>';
  const loadingInterval = await startLoading();

  try {
    // Load story content
    const storyData = await loadContent(storyId);

    await stopLoading(loadingInterval);

    // Create a synthetic profile for the adult recorder
    const adultAvatarUrl = adultRecordingParams.avatarUrl || '/svg/components/GrownUp-1.svg';
    const adultProfile = {
      id: adultRecordingParams.userId || 'adult-recorder',
      name: displayName,
      avatarUrl: adultAvatarUrl,
      isGuest: false,
      isPreview: false,
      isAdultRecorder: true,
    };

    // Hide navigation bar visually but keep space (prevents book from expanding and clipping page numbers)
    const navBar = document.querySelector('.navigation-bar');
    if (navBar) navBar.style.visibility = 'hidden';
    const navContainer = document.getElementById('navigation-container');
    if (navContainer) navContainer.style.visibility = 'hidden';

    // Set reader-mode for orange background (consistent with child-app reader)
    const app = document.getElementById('app');
    if (app) app.classList.add('reader-mode');

    // Create a synthetic module object
    const moduleObj = {
      id: storyId,
      title: storyData.title || 'Story',
      coverImageUrl: storyData.coverImageUrl || storyData.coverImage || null,
      thumbnailUrl: storyData.thumbnailUrl || null,
      storyContent: storyData,
    };

    // Store context
    currentProfile = adultProfile;
    currentModule = moduleObj;
    currentStoryData = storyData;
    resetReaderState(); // Drop any recordings/mode left over from the previous story or profile

    // Fetch existing recordings so playback works for already-recorded pages
    try {
      const { adultRecordings } = await fetchStoryRecordings(storyId, {
        recordingType: 'family_narration',
      });
      fetchedAdultRecordings = adultRecordings;
      // Populate pageRecordings with existing family narration recordings
      adultRecordings.forEach((narrator) => {
        narrator.pages.forEach((audioUrl, pageNum) => {
          const flipIdx = pageNum + PREPENDED_PAGE_COUNT - 1;
          if (!pageRecordings.has(flipIdx)) {
            pageRecordings.set(flipIdx, { url: audioUrl, status: 'ready', storyPage: pageNum });
          }
        });
      });
      console.log('🎵 Adult recordings loaded for playback:', adultRecordings.length, 'narrators');
    } catch (err) {
      console.warn('⚠️ Failed to fetch existing recordings:', err.message);
    }

    // Render the story interface (this builds the flipbook + audio controls)
    renderStoryInterface(adultProfile, moduleObj, storyData);

    // After render, force into record mode (skip the Audio Mode Menu)
    // Wait for PageFlip to initialize
    setTimeout(() => {
      // Set mode to record — but DON'T show audio controls yet.
      // They'll be shown by the onFlip handler when we land on the first story page.
      currentReadingMode = 'record';

      // Update avatar to adult's profile picture
      const avatarImg = document.getElementById('audioAvatar');
      if (avatarImg) {
        avatarImg.src = adultAvatarUrl;
        if (
          adultAvatarUrl.includes('googleusercontent.com') ||
          adultAvatarUrl.includes('facebook.com')
        ) {
          avatarImg.referrerPolicy = 'no-referrer';
          avatarImg.crossOrigin = 'anonymous';
        }
      }

      // Flip past the index page to the first story page FIRST,
      // then resume the recording session (which shows coach + controls).
      // This ensures nothing shows on the index/glossary page.
      if (pageFlipInstance) {
        pageFlipInstance.flip(PREPENDED_PAGE_COUNT);
      }

      // Resume after flip — coach and controls will show on the story page
      setTimeout(() => {
        resumeRecordingSession();
        // Now show audio controls (we're on a story page)
        const audioControls = document.getElementById('audioControls');
        if (audioControls) audioControls.classList.add('audio-controls--visible');
        // Enable record button
        const recordBtn = document.getElementById('recordBtn');
        if (recordBtn) recordBtn.disabled = false;
      }, 500);

      console.log('🎙️ Adult recording mode active, display name:', displayName);
    }, 1000);
  } catch (error) {
    console.error('❌ Failed to load story for adult recording:', error);
    await stopLoading(loadingInterval);
    contentContainer.innerHTML = `
      <div class="adult-recording-setup">
        <p style="color: red; text-align: center;">Failed to load story. Please try again.</p>
      </div>
    `;
  }
}

/**
 * Render story reader interface
 */
export async function renderStoryReader(profile, module) {
  // Render into content-container, not app (to preserve navigation)
  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  // Debug: Log what we received
  console.log('📖 StoryReader received:', {
    profile: profile,
    module: module,
    moduleId: module?.id,
    moduleKeys: module ? Object.keys(module) : 'null',
  });

  // Store current context
  currentProfile = profile;
  currentModule = module;
  starRewardPlayed = false; // Reset for new module
  resetReaderState(); // Drop any recordings/mode left over from the previous story or profile

  // Show loading state
  contentContainer.innerHTML = `
    <div class="story-reader-container"></div>
  `;

  // Start loading animation
  const loadingInterval = await startLoadingAnimation();

  try {
    // Check if story content is already provided (e.g., in preview mode)
    let storyData;
    if (module.storyContent && module.storyContent.pages) {
      console.log('📖 Using provided story content (preview mode)');
      storyData = module.storyContent;
    } else {
      // Load story content using enhanced content service
      storyData = await loadStoryContent(module.id);
    }

    currentStoryData = storyData;

    // Fetch existing recordings from backend (child + adult narrations)
    // Awaited so the Audio Mode Menu can show adult recordings
    if (!profile.isGuest && !profile.isPreview) {
      try {
        // Adult narrations are family-wide; child recordings are private to
        // this profile, so childProfileId filters out siblings' recordings.
        const { adultRecordings, childRecordings } = await fetchStoryRecordings(module.id, {
          childProfileId: profile.id,
        });
        fetchedAdultRecordings = adultRecordings;
        // Attach adult recordings to story data so the audio menu shows them
        currentStoryData.adultRecordings = adultRecordings;

        // Seed pageRecordings with what *this* reader has already recorded.
        //
        // For an adult narrator that means their own family narration. Child
        // recordings are deliberately excluded: an adult profile that once
        // recorded through the child path owns rows of type "child", and those
        // passed the childProfileId filter, so the coach counted pages the
        // narrator had never narrated and enabled "send" with nothing to send.
        if (isAdultNarrator()) {
          const own = adultRecordings.find((n) => n.profileId === profile.id);
          own?.pages.forEach((audioUrl, pageNum) => {
            const flipIdx = pageNum + PREPENDED_PAGE_COUNT - 1;
            if (!pageRecordings.has(flipIdx)) {
              pageRecordings.set(flipIdx, { url: audioUrl, status: 'ready', storyPage: pageNum });
            }
          });
        } else {
          childRecordings.forEach((rec, pageNum) => {
            const flipIdx = pageNum + PREPENDED_PAGE_COUNT - 1;
            if (!pageRecordings.has(flipIdx)) {
              pageRecordings.set(flipIdx, rec);
            }
          });
        }
        console.log(
          '🎵 Recordings loaded from backend:',
          adultRecordings.length,
          'adult,',
          childRecordings.size,
          'child'
        );
      } catch (err) {
        console.warn('⚠️ Failed to fetch recordings (continuing without):', err.message);
      }
    }

    // Track story start (skip for preview/guest profiles)
    if (!profile.isGuest && !profile.isPreview) {
      await trackProgress(profile.id, module.id, 'story-start', 'story_started', 0, {
        title: module.title,
      });
    }

    // Stop loading animation
    await stopLoadingAnimation(loadingInterval);

    // Render the story reader interface
    renderStoryInterface(profile, module, storyData);

  } catch (error) {
    console.error('❌ Error loading story:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      moduleId: module?.id,
      profile: profile?.id,
    });
    // Stop loading animation
    await stopLoadingAnimation(loadingInterval);

    // Check if content is not ready yet
    if (error.message === 'CONTENT_NOT_READY') {
      renderContentNotReady(profile, module);
    } else {
      renderStoryError(profile, module, error);
    }
  }
}

/**
 * Render story reader interface — flipbook loads immediately with cover + index + story pages
 */
function renderStoryInterface(profile, module, storyData) {
  // Render into content-container, not app (to preserve navigation)
  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  // Get avatar URL
  let avatarUrl = profile.avatarUrl || profile.avatar || '/svg/branding/KassieAvatar.svg';
  if (avatarUrl && !avatarUrl.startsWith('http')) {
    avatarUrl = `/svg/components/${avatarUrl}`;
  }

  // Get cover image URL
  const coverImageUrl = module.coverImageUrl || module.thumbnailUrl || storyData.coverImage || null;

  console.log('🖼️ Cover image URL:', coverImageUrl);
  console.log('📦 Module data:', {
    coverImageUrl: module.coverImageUrl,
    thumbnailUrl: module.thumbnailUrl,
    storyDataCoverImage: storyData.coverImage,
  });

  // Check if any page has audio (or debug mode is enabled)
  const hasAudio =
    DEBUG_AUDIO_CONTROLS || storyData.pages?.some((page) => page.audioUrl || page.narrationUrl);

  console.log('🔊 Audio check:', {
    DEBUG_AUDIO_CONTROLS,
    hasAudio,
    pagesWithAudio: storyData.pages?.filter((p) => p.audioUrl || p.narrationUrl).length || 0,
    totalPages: storyData.pages?.length || 0,
  });

  contentContainer.innerHTML = `
    <div class="story-reader-container">
      <!-- Story Content with Page Flip (cover + index + story pages all inside) -->
      <main class="story-content" id="storyContent">
        <div class="flip-book" id="flipBook"></div>
      </main>

      <!-- Audio Controls: Avatar → Play/Pause → Record/Stop → Progress Bar → Star -->
      ${hasAudio
      ? `
      <footer class="audio-controls" id="audioControls">
        <div class="audio-block audio-block--avatar">
          <img class="audio-avatar" id="audioAvatar" src="/svg/branding/KassieAvatar.svg" alt="" aria-hidden="true" />
          <div class="volume-popup" id="volumePopup">
            <input type="range" id="volumeSlider" class="volume-slider" min="0" max="100" value="100" orient="vertical" />
          </div>
        </div>
        <div class="audio-block audio-block--play">
          <button class="audio-btn audio-btn--play" id="playPauseBtn" aria-label="Play audio">
            <img id="playIcon" src="/svg/buttons/PlayButton-2.svg" alt="Play" />
            <img id="pauseIcon" src="/svg/buttons/PauseButton-2.svg" alt="Pause" class="audio-icon--hidden" />
          </button>
        </div>
        <div class="audio-block audio-block--record">
          <button class="audio-btn audio-btn--record" id="recordBtn" aria-label="Record audio" disabled>
            <img id="recordIcon" src="/svg/buttons/RecordButton-2.svg" alt="Record" />
            <img id="stopIcon" src="/svg/buttons/StopButton-2.svg" alt="Stop" class="audio-icon--hidden" />
            <img id="stopActiveIcon" src="/svg/buttons/StopButtonActive-2.svg" alt="Stop" class="audio-icon--hidden" />
          </button>
        </div>
        <div class="audio-block audio-block--progress">
          <div class="audio-progress-bar">
            <div class="audio-progress-fill" id="audioProgressFill"></div>
          </div>
        </div>
        <div class="audio-block audio-block--star">
          <button class="audio-btn audio-btn--star" id="starBtn" aria-label="Favorite">
            <img src="/svg/components/YellowStar.svg" alt="Star" />
          </button>
        </div>
      </footer>
      `
      : ''
    }
    </div>
  `;

  // Initialize page flip immediately (cover is the first page)
  // Note: bindNavigationEvents() is called inside initializeStoryPageFlip after PageFlip loads
  initializeStoryPageFlip(profile, module, storyData);
}

/**
 * Handle index page button clicks inside the flipbook.
 * Called via event delegation from the flipbook container.
 * Shows audio controls and stores the selected mode.
 * @param {string} mode - Selected mode ('read', 'listen', 'record', 'listen-adult')
 */
function handleIndexModeSelection(mode, extra = {}) {
  // If recording is active and user switches to a non-record mode, stop it first
  if (isRecording && mode !== 'record') {
    toggleRecording();
    console.log('⏹️ Stopped active recording due to mode switch to:', mode);
  }

  currentReadingMode = mode;
  console.log('📖 Audio mode selected:', mode, extra);

  // Show audio controls now that a mode is selected (except for read-only)
  const audioControls = document.getElementById('audioControls');
  if (audioControls) {
    if (mode === 'read') {
      audioControls.classList.remove('audio-controls--visible');
    } else {
      audioControls.classList.add('audio-controls--visible');
    }
  }

  // Hide record button in read-listen mode (read + play, no recording)
  const recordBlock = document.querySelector('.audio-block--record');
  if (recordBlock) {
    recordBlock.style.display = mode === 'read-listen' ? 'none' : '';
  }

  // Store adult recording info if applicable
  if (extra.adultIndex !== undefined) {
    currentAdultRecordingIndex = extra.adultIndex;
    currentAdultRecordingName = extra.adultName;
  }

  // Update avatar based on selected mode
  updateAudioAvatar(mode);

  // Update record button state based on mode
  updateRecordButtonState(mode);

  // Entering record mode: resume where they left off, once the flip settles.
  // Adults always resume; a child only when they asked for it via the
  // "Remember my story" card. Registered before the single-page rebuild below
  // returns early, so it runs on both entry paths.
  if (mode === 'record' && (isAdultNarrator() || extra.resume)) {
    setTimeout(() => resumeRecordingSession(), 1200);
  }

  // Force single-page mode when recording (both adult and child need page-by-page)
  if (mode === 'record' && isDoublePageMode()) {
    console.log('📖 Switching to single-page mode for recording');
    // Temporarily set forcePortrait flag so reinitializePageFlip uses single-page
    currentProfile._forceRecordPortrait = true;

    // Mark the reader as single-page BEFORE rebuilding. The desktop rule in
    // reader.css pins .stf__wrapper to the double-page ratio with !important
    // (calc(70vh * 10 / 7) ≈ 1059px). .stf__block inherits that via width:100%,
    // and StPageFlip's calculateBoundsRect only picks portrait when the block is
    // narrower than two pages — so at 1058 vs 2×529 it stays landscape and
    // renders a spread. Setting this first lets the single-page override apply
    // to the wrapper as soon as the library creates it.
    setReaderPageMode('single');

    reinitializePageFlip(PREPENDED_PAGE_COUNT);
    return; // reinitializePageFlip will trigger onFlip which handles the rest
  }

  // Flip past the index page to the first story page
  if (pageFlipInstance) {
    const currentPage = pageFlipInstance.getCurrentPageIndex();

    // In double-page mode, the glossary (page 1) and first story page (page 2)
    // may already be showing as a spread. flip(2) won't trigger onFlip in that case,
    // so the auto-play timer from onFlip never fires.
    const alreadyOnFirstStorySpread =
      isDoublePageMode() &&
      (currentPage === PREPENDED_PAGE_COUNT || currentPage === PREPENDED_PAGE_COUNT - 1);

    if (alreadyOnFirstStorySpread) {
      console.log('📖 Already on first story spread in double-page mode — skipping flip');
    } else {
      pageFlipInstance.flip(PREPENDED_PAGE_COUNT);
    }

    // In listen modes, auto-play page 1 audio after a short delay.
    // Only schedule here if flip was skipped (alreadyOnFirstStorySpread).
    // Otherwise, the onFlip callback handles auto-play scheduling.
    if ((mode === 'listen' || mode === 'listen-adult') && alreadyOnFirstStorySpread) {
      clearAutoPlayTimer();
      const targetPage = PREPENDED_PAGE_COUNT; // first story page
      autoPlayTimer = setTimeout(() => {
        autoPlayTimer = null;
        if (pageFlipInstance) {
          const nowPage = pageFlipInstance.getCurrentPageIndex();
          // Verify we're on the first story spread
          if (nowPage === targetPage || nowPage === targetPage - 1) {
            updatePlayButtonForPage(nowPage);
            toggleAudioPlayback();
            console.log('🔊 Auto-playing page 1 audio after mode selection');
          }
        }
      }, 2000);
    }
  }

  console.log('📖 Starting story in mode:', mode);

  // Save audio source preference per story in localStorage (Req 13.3)
  if (currentModule?.id) {
    const prefKey = `kassie_audio_pref_${currentModule.id}`;
    const pref = { mode };
    if (extra.adultIndex !== undefined) {
      pref.adultIndex = extra.adultIndex;
      pref.adultName = extra.adultName;
    }
    try {
      localStorage.setItem(prefKey, JSON.stringify(pref));
    } catch (e) {
      /* quota exceeded — ignore */
    }
  }
}

/**
 * Apply saved audio preference for the current story.
 * If the user previously selected a mode for this story, visually select
 * the corresponding card on the index page (but don't auto-start — let the
 * user confirm by tapping the card).
 */
function applySavedAudioPreference(flipBookContainer) {
  // Always default to "Lees sonder klank" (read without sound) every time a book is opened
  applyDefaultReadMode(flipBookContainer);
}

/**
 * Apply default "read without sound" mode selection.
 * Visually selects the read card and sets currentReadingMode to 'read'.
 */
function applyDefaultReadMode(flipBookContainer) {
  // Skip default read mode in adult recording mode — record is pre-selected
  if (document.documentElement.dataset.adultRecordMode === 'true') {
    return;
  }

  setTimeout(() => {
    const readCard = flipBookContainer.querySelector('[data-mode="read"]');
    if (readCard) {
      readCard.classList.add('audio-mode-card--selected');
      currentReadingMode = 'read';
      console.log('📖 Default read mode applied');
    }
  }, 500);
}

/**
 * Update the audio controls avatar based on the selected mode.
 * Publisher → KassieAvatar, Adult → GrownUp, Child record → child's avatar
 */
function updateAudioAvatar(mode) {
  const avatarImg = document.getElementById('audioAvatar');
  if (!avatarImg) return;

  if (mode === 'listen') {
    // Publisher narration
    avatarImg.src = '/svg/branding/KassieAvatar.svg';
  } else if (mode === 'read-listen') {
    // Read and listen — same icon as read-only
    avatarImg.src = '/svg/components/IsolationMode.svg';
  } else if (mode === 'listen-adult') {
    // Adult family recording — use the avatar of the currently selected narrator
    const adultIdx = currentAdultRecordingIndex || 0;
    const adultRec = fetchedAdultRecordings[adultIdx];
    let adultAvatar = adultRec?.avatarUrl || '/svg/components/GrownUp-1.svg';
    if (adultAvatar && !adultAvatar.startsWith('http') && !adultAvatar.startsWith('/')) {
      adultAvatar = `/svg/components/${adultAvatar}`;
    }
    avatarImg.src = adultAvatar;
  } else if (mode === 'record') {
    // Child recording — use child's own avatar
    let childAvatar =
      currentProfile?.avatarUrl || currentProfile?.avatar || '/svg/branding/KassieAvatar.svg';
    if (childAvatar && !childAvatar.startsWith('http')) {
      childAvatar = `/svg/components/${childAvatar}`;
    }
    avatarImg.src = childAvatar;
  }
}

/**
 * Update record button enabled/disabled state based on mode.
 * Record button is only enabled in 'record' mode.
 */
function updateRecordButtonState(mode) {
  const recordBtn = document.getElementById('recordBtn');
  if (!recordBtn) return;

  if (mode === 'record') {
    recordBtn.disabled = false;
  } else {
    recordBtn.disabled = true;
  }
}

/**
 * Bind click events on index page buttons inside the flipbook.
 * Uses event delegation on the flipbook container.
 */
function bindIndexPageEvents(flipBookContainer) {
  flipBookContainer.addEventListener('click', (e) => {
    const card = e.target.closest('.audio-mode-card');
    if (card) {
      const mode = card.dataset.mode;
      if (mode) {
        // Visually indicate selected card
        flipBookContainer.querySelectorAll('.audio-mode-card').forEach((c) => {
          c.classList.remove('audio-mode-card--selected');
          c.classList.remove('audio-mode-card--preferred');
        });
        card.classList.add('audio-mode-card--selected');

        // Pass extra data for adult mode
        const extra = {};
        if (card.dataset.adultIndex !== undefined) {
          extra.adultIndex = parseInt(card.dataset.adultIndex, 10);
          extra.adultName = card.dataset.adultName;
        }

        // "Remember my story" is record mode that opens where the child left
        // off, so it resolves to the same mode with a flag rather than adding a
        // second dispatch path.
        if (mode === 'record-resume') {
          handleIndexModeSelection('record', { ...extra, resume: true });
        } else {
          handleIndexModeSelection(mode, extra);
        }
      }
    }
  });
}

// Audio controls width is 100% via CSS — no JS width matching needed

// Call on window resize to reinitialize PageFlip with new dimensions
let resizeTimeout;
window.addEventListener('resize', () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    if (pageFlipInstance && currentStoryData && currentProfile && currentModule) {
      // Don't reinitialize during active recording — it destroys the reader state
      if (isRecording) {
        recDbg.warn('Resize ignored — recording in progress');
        return;
      }

      console.log('📐 Window resized - reinitializing PageFlip');

      // Get current page before destroying
      const currentPage = pageFlipInstance.getCurrentPageIndex();

      // Schalk: st page flip dissapears on resize or rotation because it is being destroyed here...
      // Destroy old instance
      // pageFlipInstance.destroy();
      // pageFlipInstance = null;
      pageFlipInstance.innerHTML = '';

      // Clear the container
      const flipBookContainer = document.getElementById('flipBook');
      if (flipBookContainer) {
        flipBookContainer.innerHTML = '';

        // Force visibility and display
        flipBookContainer.style.display = 'block';
        flipBookContainer.style.visibility = 'visible';
        flipBookContainer.style.opacity = '1';

        // Reinitialize with new dimensions
        try {
          reinitializePageFlip(currentPage);
        } catch (e) {
          console.error('Rebounce Error: ', e);
        }
      } else {
        console.error(
          'flipBook container was destroyed and not recreated!',
          'flipBook = ' + flipBookContainer
        );
      }
    }
  }, 300); // Debounce resize events
});

/**
 * Reinitialize PageFlip after resize
 */
function reinitializePageFlip(startPage = 0) {
  const flipBookContainer = document.getElementById('flipBook');
  if (!flipBookContainer || !currentStoryData) return;

  // Reset cover centering state — new dimensions will be calculated after init
  if (coverAnimationTimer !== null) {
    clearTimeout(coverAnimationTimer);
    coverAnimationTimer = null;
  }
  coverCurrentOffset = 0;
  flipBookContainer.style.transform = '';

  // Get cover image URL from module or story data
  const coverImageUrl =
    currentModule.coverImageUrl ||
    currentModule.thumbnailUrl ||
    currentStoryData.coverImage ||
    null;

  // Rebuild audio menu context
  let childAvatarUrl =
    currentProfile.avatarUrl || currentProfile.avatar || '/svg/branding/KassieAvatar.svg';
  if (childAvatarUrl && !childAvatarUrl.startsWith('http')) {
    childAvatarUrl = `/svg/components/${childAvatarUrl}`;
  }
  const audioMenuContext = {
    hasPublisherAudio: currentStoryData.pages?.some((page) => page.audioUrl || page.narrationUrl),
    childAvatarUrl,
    adultRecordings: currentStoryData.adultRecordings || [],
    profileType: currentProfile?.type || 'child',
  };

  // Create page elements with audio menu context
  const pageElements = createPageElements(
    currentStoryData.pages,
    currentModule.title,
    coverImageUrl,
    audioMenuContext
  );

  // Add pages to container
  pageElements.forEach((page) => {
    flipBookContainer.appendChild(page);
  });

  // Wait for DOM to be fully rendered before initializing
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Initialize PageFlip
      pageFlipInstance = initializePageFlip(flipBookContainer, currentStoryData.pages, {
        forcePortrait: shouldForcePortrait(),
        onFlip: (pageIndex) => {
          updateCoverCentering();
          updatePageIndicator(pageIndex);
          trackPageView(currentProfile, currentModule, pageIndex);
          // Preload audio for this page and next
          preloadPageAudio(pageIndex);
          updatePlayButtonForPage(pageIndex);
          // Stop star bounce on page turn
          stopStarBounce();
          // Stop current audio when page changes
          if (isAudioPlaying && audioElement) {
            audioElement.pause();
            isAudioPlaying = false;
            const playIcon = document.getElementById('playIcon');
            const pauseIcon = document.getElementById('pauseIcon');
            if (playIcon) playIcon.classList.remove('audio-icon--hidden');
            if (pauseIcon) pauseIcon.classList.add('audio-icon--hidden');
          }
          // Record-mode page turns — shared with the other PageFlip instance so
          // both behave identically (see handleRecordModeFlip). Runs before the
          // back-cover handling below, which would otherwise clear the mode
          // out from under it.
          const isFinishingRecording = isAdultNarrator() && currentReadingMode === 'record';
          handleRecordModeFlip(pageIndex);

          // Back cover → auto-close to front cover.
          //
          // Skipped while an adult is recording: this used to null
          // currentReadingMode before handleRecordModeFlip ran, so reaching the
          // last page silently cancelled record mode. sendRecordingAndFinish
          // never fired, no celebration appeared, and 1.5s later the book flipped
          // back to the cover still asking whether to record — exactly what
          // Margie saw. Finishing is handled by sendRecordingAndFinish, which
          // waits for uploads and then returns to the library.
          const totalFlipPages = pageFlipInstance ? pageFlipInstance.getPageCount() : 0;
          if (totalFlipPages > 0 && pageIndex >= totalFlipPages - 1 && !isFinishingRecording) {
            currentReadingMode = null;
            const audioControls = document.getElementById('audioControls');
            if (audioControls) audioControls.classList.remove('audio-controls--visible');
            setTimeout(() => {
              if (pageFlipInstance) pageFlipInstance.flip(0);
            }, 1500);
          }
          // Reset last recorded page when navigating to a different spread
          lastRecordedPageIndex = null;
        },
        onChangeState: (state) => {
          console.log('Page flip state:', state);
        },
        onChangeOrientation: (orientation) => {
          console.log('Orientation changed:', orientation);
          updateCoverCentering();
        },
      });

      if (pageFlipInstance) {
        // Load pages from HTML elements
        const pages = flipBookContainer.querySelectorAll('.page');
        pageFlipInstance.loadFromHTML(pages);

        // Go to the page we were on
        if (startPage > 0) {
          setTimeout(() => {
            pageFlipInstance.flip(startPage);
          }, 100);
        }

        console.log('✅ PageFlip reinitialized after resize');
      }
    });
  });
}

/**
 * Initialize page flip for story
 */
function initializeStoryPageFlip(profile, module, storyData) {
  const flipBookContainer = document.getElementById('flipBook');

  if (!flipBookContainer) {
    console.error('Flip book container not found');
    return;
  }

  // Load the St PageFlip library dynamically
  loadPageFlipLibrary()
    .then(() => {
      // Get cover image URL from module or story data
      const coverImageUrl =
        module.coverImageUrl || module.thumbnailUrl || storyData.coverImage || null;

      // Build audio menu context from story data and profile
      let childAvatarUrl = profile.avatarUrl || profile.avatar || '/svg/branding/KassieAvatar.svg';
      if (childAvatarUrl && !childAvatarUrl.startsWith('http')) {
        childAvatarUrl = `/svg/components/${childAvatarUrl}`;
      }

      const hasPublisherAudio = storyData.pages?.some((page) => page.audioUrl || page.narrationUrl);
      const adultRecordings = storyData.adultRecordings || []; // Array of {name, avatarUrl}

      const audioMenuContext = {
        hasPublisherAudio,
        childAvatarUrl,
        adultRecordings,
        profileType: currentProfile?.type || 'child',
        // Safe to read here: renderStoryReader awaits fetchStoryRecordings and
        // seeds pageRecordings before this runs, so a returning child's own
        // recordings are already known.
        resumePage: computeResumePage(),
      };

      // Create page elements with audio menu context
      const pageElements = createPageElements(
        storyData.pages,
        module.title,
        coverImageUrl,
        audioMenuContext
      );

      // Add pages to container
      pageElements.forEach((page) => {
        flipBookContainer.appendChild(page);
      });

      // Wait for DOM to be fully rendered before initializing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Initialize PageFlip with callbacks
          // Force single-page (portrait) mode for adult recorders to maintain 1:1.4 ratio
          const pageFlipOptions = {
            forcePortrait: shouldForcePortrait(),
            onFlip: (pageIndex) => {
              updateCoverCentering();
              updatePageIndicator(pageIndex);
              trackPageView(profile, module, pageIndex);

              // Preload star reward images at the midpoint of the module
              const totalStoryPages = storyData.pages?.length || 0;
              const midpointFlipIndex = PREPENDED_PAGE_COUNT + Math.floor(totalStoryPages / 2);
              if (pageIndex >= midpointFlipIndex) {
                preloadStarImages();
              }

              // Trigger star reward on last story page (catches mobile/tablet where back cover event may not fire)
              const lastStoryFlipIndex = PREPENDED_PAGE_COUNT + totalStoryPages - 1;
              if (pageIndex >= lastStoryFlipIndex && !starRewardPlayed && !isAdultNarrator()) {
                // Stars now triggered on back cover arrival instead
                // Just preload here to ensure smooth playback
                preloadStarImages();
              }

              // Determine if we're on a cover page (front or back)
              const totalFlipPages = pageFlipInstance ? pageFlipInstance.getPageCount() : 0;
              const isOnBackCover = totalFlipPages > 0 && pageIndex >= totalFlipPages - 1;
              const isOnFrontCover = pageIndex === 0;

              // Hide audio controls on cover and glossary pages (pre-story pages)
              // Also hide on back cover
              const audioControls = document.getElementById('audioControls');
              if (audioControls) {
                if (isOnBackCover || isOnFrontCover || pageIndex < PREPENDED_PAGE_COUNT) {
                  // On front cover, index/glossary, or back cover — always hide controls
                  audioControls.classList.remove('audio-controls--visible');
                } else if (currentReadingMode && currentReadingMode !== 'read') {
                  // Audio mode selected and on a story page — show controls
                  audioControls.classList.add('audio-controls--visible');
                }
              }

              // Hide the coach on covers and pre-story pages
              if (isOnBackCover || isOnFrontCover || pageIndex < PREPENDED_PAGE_COUNT) {
                hideCoach();
                clearCompletionToast();
              }

              // When arriving on the back cover, auto-flip to front cover after a delay
              // This gives the "book closing" effect — the cover slides to center
              if (isOnBackCover) {
                console.log('📖 Reached back cover — closing book to front cover');
                // Reset reading mode so the index page menu shows again
                currentReadingMode = null;

                // Star reward is a child reward: it flies to the achievements
                // trophy and plays its own wow.mp3. Adults get their own burst
                // from showRecordingComplete, so gate on every adult path here
                // or the sound fires twice (#146).
                if (!starRewardPlayed && !isAdultNarrator()) {
                  starRewardPlayed = true;
                  playStarReward().then(() => {
                    console.log('⭐ Star reward animation complete');
                    resetStarPreload();
                  });
                }

                setTimeout(() => {
                  if (pageFlipInstance) {
                    pageFlipInstance.flip(0);
                  }

                  // Show "Close Recorder" button for adult recorders after book closes
                  if (currentProfile?.isAdultRecorder) {
                    showCloseRecorderButton();
                  }
                }, 1500);
              }

              // Preload audio for this page and next
              preloadPageAudio(pageIndex);
              updatePlayButtonForPage(pageIndex);
              // Clear any pending auto-play timer from previous page
              clearAutoPlayTimer();
              // Stop star bounce on page turn
              stopStarBounce();
              // Stop current audio when page changes (Req 3.6)
              const wasPlaying = isAudioPlaying;
              if (isAudioPlaying && audioElement) {
                audioElement.pause();
                if (audioElement.currentTime) audioElement.currentTime = 0;
                isAudioPlaying = false;
                const playIcon = document.getElementById('playIcon');
                const pauseIcon = document.getElementById('pauseIcon');
                if (playIcon) playIcon.classList.remove('audio-icon--hidden');
                if (pauseIcon) pauseIcon.classList.add('audio-icon--hidden');
                // Cancel progress tracking
                if (audioProgressRAF) {
                  cancelAnimationFrame(audioProgressRAF);
                  audioProgressRAF = null;
                }
              }
              // Reset progress bar on page turn
              updateAudioProgress(0, 1);
              // Re-enable record button on page turn (only in record mode)
              const recordBtn = document.getElementById('recordBtn');
              if (recordBtn) recordBtn.disabled = currentReadingMode !== 'record';
              // Auto-play new page audio after 2-second delay in listen mode (Req 3.5, 3.6)
              if (
                currentReadingMode === 'listen' ||
                currentReadingMode === 'read-listen' ||
                currentReadingMode === 'listen-adult'
              ) {
                const storyIdx = getStoryPageIndex(pageIndex);
                let hasAudioForPage = false;

                if (currentReadingMode === 'listen-adult') {
                  // Check adult recordings
                  hasAudioForPage = !!getAdultRecordingUrl(storyIdx + 1);
                  if (!hasAudioForPage) {
                    hasAudioForPage = !!getAdultRecordingUrl(getStoryPageIndex(pageIndex + 1) + 1);
                  }
                } else {
                  // Check publisher audio
                  let audioUrl = getPageAudioUrl(storyIdx);
                  if (!audioUrl) {
                    audioUrl = getPageAudioUrl(getStoryPageIndex(pageIndex + 1));
                  }
                  hasAudioForPage = !!audioUrl;
                }

                if (hasAudioForPage) {
                  console.log('🔊 Page turned in listen mode — auto-play in 300ms');
                  autoPlayTimer = setTimeout(() => {
                    autoPlayTimer = null;
                    // Verify still on same page
                    if (pageFlipInstance && pageFlipInstance.getCurrentPageIndex() === pageIndex) {
                      toggleAudioPlayback();
                    }
                  }, 300);
                }
              }

              // In record mode, if not actively recording and the new page
              // already has a recording, auto-play it so the child can listen
              // to their own work hands-free (same star-to-advance pattern as
              // listen mode). Only fires when idle — never interrupts an active
              // recording session.
              if (currentReadingMode === 'record' && !isRecording) {
                const rec = pageRecordings.get(pageIndex) ||
                  (isDoublePageMode() ? pageRecordings.get(pageIndex + 1) : null);
                if (rec && rec.status === 'ready' && rec.url) {
                  // Don't auto-play on the first page flip for adults (initial entry)
                  // Only auto-play when user actively navigates (star button or page turn)
                  if (!isAdultNarrator() || previousFlipIndex !== null) {
                    console.log('🔊 Page turned in record mode (not recording) — auto-play existing recording');
                    autoPlayTimer = setTimeout(() => {
                      autoPlayTimer = null;
                      if (pageFlipInstance && pageFlipInstance.getCurrentPageIndex() === pageIndex && !isRecording) {
                        toggleAudioPlayback();
                      }
                    }, 300);
                  }
                }
              }
              // Record-mode page turns (Req 6.1) — shared with the instance
              // created by reinitializePageFlip so both behave identically
              handleRecordModeFlip(pageIndex);
              // Reset last recorded page when navigating to a different spread
              lastRecordedPageIndex = null;
            },
            onChangeState: (state) => {
              console.log('Page flip state:', state);
            },
            onChangeOrientation: (orientation) => {
              console.log('Orientation changed:', orientation);
              updateCoverCentering();
            },
          };

          pageFlipInstance = initializePageFlip(
            flipBookContainer,
            storyData.pages,
            pageFlipOptions
          );

          if (pageFlipInstance) {
            // Load pages from HTML elements
            const pages = flipBookContainer.querySelectorAll('.page');
            pageFlipInstance.loadFromHTML(pages);

            // Force multiple updates to ensure proper sizing
            // Note: pageFlip.js also forces stf__wrapper styles at 100ms,
            // so we re-center after each update to prevent the cover from jumping.
            setTimeout(() => {
              if (pageFlipInstance) {
                pageFlipInstance.update();
                updateCoverCentering();
                console.log('✅ PageFlip updated (100ms)');
              }
            }, 100);

            setTimeout(() => {
              if (pageFlipInstance) {
                pageFlipInstance.update();
                updateCoverCentering();
                console.log('✅ PageFlip updated (300ms)');
              }
            }, 300);

            setTimeout(() => {
              if (pageFlipInstance) {
                pageFlipInstance.update();
                updateCoverCentering();
                console.log('✅ PageFlip final update (500ms)');
              }
            }, 500);

            // Safety: force-hide loader 600ms after PageFlip loads
            setTimeout(() => {
              window.hideLoader();
              console.log('🧹 Post-load loader hide (safety)');
            }, 600);

            // Bind navigation events
            bindNavigationEvents();

            // Bind index page button clicks via event delegation
            bindIndexPageEvents(flipBookContainer);

            // Auto-select saved audio preference for this story (Req 13.3)
            applySavedAudioPreference(flipBookContainer);

            console.log('✅ Story reader initialized with page flip');
          } else {
            console.error('❌ Failed to initialize page flip');
            showFallbackReader(profile, module, storyData);
          }
        });
      });
    })
    .catch((error) => {
      console.error('Failed to load PageFlip library:', error);
      showFallbackReader(profile, module, storyData);
    });
}

/**
 * Load PageFlip library dynamically
 */
function loadPageFlipLibrary() {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof St !== 'undefined' && St.PageFlip) {
      resolve();
      return;
    }

    // Load the script from public folder (works in both dev and production)
    const script = document.createElement('script');
    script.src = '/page-flip.browser.js';
    script.onload = () => {
      if (typeof St !== 'undefined' && St.PageFlip) {
        resolve();
      } else {
        reject(new Error('St.PageFlip not available after script load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load page-flip script'));
    document.head.appendChild(script);
  });
}

/**
 * Bind navigation button events
 */
function bindNavigationEvents() {
  const playPauseBtn = document.getElementById('playPauseBtn');
  const prevPageBtn = document.getElementById('prevPageBtn');
  const nextPageBtn = document.getElementById('nextPageBtn');
  const starBtn = document.getElementById('starBtn');

  // Navigation is now handled by the persistent NavigationBar component
  // No need to bind navigation events here

  // Play/Pause button
  if (playPauseBtn) {
    playPauseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleAudioPlayback();
    });
  }

  // Previous page button
  if (prevPageBtn) {
    prevPageBtn.addEventListener('click', () => {
      if (pageFlipInstance) {
        prevPage(pageFlipInstance);
      }
    });
  }

  // Next page button
  if (nextPageBtn) {
    nextPageBtn.addEventListener('click', () => {
      if (pageFlipInstance) {
        nextPage(pageFlipInstance);
      }
    });
  }

  // Star button
  if (starBtn) {
    starBtn.addEventListener('click', () => {
      recDbg.log('⭐ Star button clicked', { isRecording, currentRecordingPageIndex, lastRecordedPageIndex });
      if (pageFlipInstance) {
        nextPage(pageFlipInstance);
      }
    });
  }

  // Record button
  const recordBtn = document.getElementById('recordBtn');
  if (recordBtn) {
    recordBtn.addEventListener('click', (e) => {
      console.log(
        '🎙️ Record button clicked, isRecording:',
        isRecording,
        'disabled:',
        recordBtn.disabled
      );
      e.stopPropagation(); // Prevent event from bubbling to page flip
      toggleRecording();
    });
  }

  // Progress bar seek (Req 3.7)
  const progressBar = document.querySelector('.audio-progress-bar');
  if (progressBar) {
    progressBar.addEventListener('click', (e) => {
      if (!audioElement || audioElement._isMock) return;
      if (isRecording) return; // No seeking during recording
      if (!audioElement.duration || !isFinite(audioElement.duration)) return;
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, clickX / rect.width));
      audioElement.currentTime = ratio * audioElement.duration;
      updateAudioProgress(audioElement.currentTime, audioElement.duration);
      console.log('🔊 Seeked to', formatTime(audioElement.currentTime));
    });
  }

  // Volume control — avatar click toggles vertical slider
  const audioAvatar = document.getElementById('audioAvatar');
  const volumePopup = document.getElementById('volumePopup');
  const volumeSlider = document.getElementById('volumeSlider');

  if (audioAvatar && volumePopup && volumeSlider) {
    audioAvatar.addEventListener('click', (e) => {
      e.stopPropagation();
      volumePopup.classList.toggle('volume-popup--visible');
    });

    volumeSlider.addEventListener('input', (e) => {
      e.stopPropagation();
      const vol = parseInt(e.target.value, 10) / 100;
      if (audioElement) audioElement.volume = vol;
      // Store volume for future audio elements
      window._kassieVolume = vol;
    });

    // Prevent clicks inside popup from closing it
    volumePopup.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // Close popup only when clicking elsewhere
    document.addEventListener('click', () => {
      volumePopup.classList.remove('volume-popup--visible');
    });
  }

  // Keyboard navigation
  document.addEventListener('keydown', handleKeyboardNavigation);

  // Back cover click — reopen the book to the first page
  const backCover = document.querySelector('.page--back-cover');
  if (backCover) {
    backCover.addEventListener('click', () => {
      if (!pageFlipInstance) return;
      const totalPages = pageFlipInstance.getPageCount();
      const currentIndex = pageFlipInstance.getCurrentPageIndex();
      // Only respond if we're actually on the back cover
      if (currentIndex >= totalPages - 1) {
        console.log('📖 Back cover clicked — reopening book to first page');
        pageFlipInstance.flip(0);
      }
    });
  }
}

/**
 * Audio fetching and caching system.
 * Loads publisher audio from the URLs provided in story page data.
 * Caches Audio objects per page so they don't re-download on replay.
 */
const pageAudioCache = new Map(); // pageIndex → { audio: Audio, status: 'loading'|'ready'|'error', url }

/**
 * Get the story page index (0-based) from a flipbook page index.
 * Returns -1 for cover/index pages.
 */
function getStoryPageIndex(flipbookPageIndex) {
  return flipbookPageIndex - PREPENDED_PAGE_COUNT;
}

/**
 * Get the audio URL for a story page from the loaded story data.
 * @param {number} storyPageIndex - 0-based story page index
 * @returns {string|null} Audio URL or null
 */
function getPageAudioUrl(storyPageIndex) {
  if (!currentStoryData || !currentStoryData.pages) return null;
  const page = currentStoryData.pages[storyPageIndex];
  return page?.audioUrl || page?.narrationUrl || null;
}

/**
 * Get the adult recording URL for a story page (1-based page number).
 * Uses the currently selected adult recording index from the Audio Mode Menu.
 * @param {number} pageNumber - 1-based story page number
 * @returns {string|null} Audio URL or null
 */
function getAdultRecordingUrl(pageNumber) {
  if (!fetchedAdultRecordings || fetchedAdultRecordings.length === 0) return null;
  const adultIdx = currentAdultRecordingIndex || 0;
  const adult = fetchedAdultRecordings[adultIdx];
  if (!adult || !adult.pages) return null;
  return adult.pages.get(pageNumber) || null;
}

/**
 * Load (or retrieve from cache) the Audio element for a flipbook page.
 * Returns a promise that resolves with { audio, status } or null if no audio.
 */
function loadPageAudio(flipbookPageIndex) {
  // Check cache first
  const cached = pageAudioCache.get(flipbookPageIndex);
  if (cached && cached.status === 'ready') {
    return Promise.resolve(cached);
  }
  if (cached && cached.status === 'loading') {
    // Already loading — return a promise that resolves when ready
    return cached.promise;
  }

  const storyIdx = getStoryPageIndex(flipbookPageIndex);
  const url = getPageAudioUrl(storyIdx);
  if (!url) return Promise.resolve(null);

  // Route audio through the device-auth proxy for secure delivery
  let audioSrc = url;
  if (url.startsWith('https://storage.googleapis.com/kassie-content/')) {
    try {
      const deviceId = localStorage.getItem('kassie_device_id');
      let familyId = null;
      try {
        const storedFamily = localStorage.getItem('authorizedFamily');
        if (storedFamily) familyId = JSON.parse(storedFamily).id;
      } catch { /* ignore */ }

      if (deviceId && familyId) {
        const hostname = window.location.hostname;
        let apiBase = 'https://api.kassie.media';
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          apiBase = 'http://localhost:8000';
        } else if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
          apiBase = 'http://localhost:8000';
        }
        const params = new URLSearchParams({
          url: url,
          device_id: deviceId,
          family_id: familyId,
        });
        audioSrc = `${apiBase}/api/device/content/audio-proxy?${params.toString()}`;
      }
    } catch { /* fallback to direct URL */ }
  }

  const audio = new Audio();
  audio.preload = 'auto';

  const entry = { audio, status: 'loading', url, promise: null };

  entry.promise = new Promise((resolve) => {
    audio.addEventListener(
      'canplaythrough',
      () => {
        entry.status = 'ready';
        console.log(`🔊 Audio ready for page ${storyIdx + 1}:`, url);
        resolve(entry);
      },
      { once: true }
    );

    audio.addEventListener(
      'error',
      (e) => {
        entry.status = 'error';
        console.error(`❌ Audio load error for page ${storyIdx + 1}:`, e);
        resolve(entry); // resolve (not reject) so callers can check status
      },
      { once: true }
    );

    audio.src = audioSrc;
    audio.load();
  });

  pageAudioCache.set(flipbookPageIndex, entry);
  return entry.promise;
}

/**
 * Preload audio for the current page and the next page (look-ahead).
 */
function preloadPageAudio(flipbookPageIndex) {
  loadPageAudio(flipbookPageIndex);
  // Look-ahead: preload next page too (also covers right page in double-page mode)
  loadPageAudio(flipbookPageIndex + 1);
  // In double-page mode, also preload the next spread
  if (isDoublePageMode()) {
    loadPageAudio(flipbookPageIndex + 2);
  }
}

/**
 * Check if StPageFlip is currently in double-page (landscape) mode.
 * Uses the PageFlip instance's orientation property rather than CSS classes.
 */
function isDoublePageMode() {
  if (!pageFlipInstance) return false;
  try {
    return pageFlipInstance.getOrientation() === 'landscape';
  } catch (e) {
    return false;
  }
}

/**
 * Center the cover page when it's the only page showing in landscape
 * (double-page) mode. In landscape, StPageFlip renders the front cover on the
 * right half of the 2×pageWidth spread, and the back cover on the left half.
 * We shift the #flipBook container to center whichever cover is showing.
 *
 * Uses a recursive setTimeout animation (~500ms total) with easing:
 *   - Ease-in when sliding to center a cover
 *   - Ease-out when sliding back to normal spread position
 */
function updateCoverCentering() {
  if (!pageFlipInstance) return;

  const flipBook = document.getElementById('flipBook');
  if (!flipBook) return;

  const inDoublePage = isDoublePageMode();
  let onFrontCover = false;
  let onBackCover = false;
  try {
    const currentIndex = pageFlipInstance.getCurrentPageIndex();
    const totalPages = pageFlipInstance.getPageCount();
    onFrontCover = currentIndex === 0;
    onBackCover = currentIndex >= totalPages - 1;
  } catch (e) {
    onFrontCover = false;
    onBackCover = false;
  }

  // The #flipBook container width = 2 × pageWidth, so half a page = container.offsetWidth / 4
  const halfPageWidth = Math.round(flipBook.offsetWidth / 4);

  // Front cover renders on right half → shift left to center
  // Back cover renders on left half → shift right to center
  let targetOffset = 0;
  if (inDoublePage && onFrontCover) {
    targetOffset = -halfPageWidth;
  } else if (inDoublePage && onBackCover) {
    targetOffset = halfPageWidth;
  }

  // If already at target (or close enough), nothing to do
  if (Math.abs(coverCurrentOffset - targetOffset) < 1) {
    coverCurrentOffset = targetOffset;
    flipBook.style.transform = targetOffset === 0 ? '' : `translateX(${targetOffset}px)`;
    return;
  }

  // Cancel any running animation
  if (coverAnimationTimer !== null) {
    clearTimeout(coverAnimationTimer);
    coverAnimationTimer = null;
  }

  // Animation config
  const duration = 500; // ms total
  const tickInterval = 16; // ~60fps
  const totalTicks = Math.ceil(duration / tickInterval);
  const startOffset = coverCurrentOffset;
  const totalDistance = targetOffset - startOffset;
  // Sliding away from center = ease-in, sliding back to center = ease-out
  const slidingToCover = Math.abs(targetOffset) > Math.abs(startOffset);
  let tick = 0;

  function animateStep() {
    tick++;
    if (tick >= totalTicks) {
      // Final frame — snap to target
      coverCurrentOffset = targetOffset;
      flipBook.style.transform = targetOffset === 0 ? '' : `translateX(${targetOffset}px)`;
      coverAnimationTimer = null;
      console.log('📐 Cover centering complete, offset:', targetOffset);
      return;
    }

    // Normalized progress 0→1
    const t = tick / totalTicks;

    // Easing functions:
    // ease-in:  t² (slow start, fast end) — for sliding to cover
    // ease-out: 1-(1-t)² (fast start, slow end) — for sliding back to spread
    const eased = slidingToCover ? t * t : 1 - (1 - t) * (1 - t);

    coverCurrentOffset = startOffset + totalDistance * eased;
    flipBook.style.transform = `translateX(${Math.round(coverCurrentOffset)}px)`;
    coverAnimationTimer = setTimeout(animateStep, tickInterval);
  }

  animateStep();
}

/**
 * In double-page mode, find which flipbook page index on the current spread has audio.
 * Returns the flipbook index of the page with audio, or -1 if neither has audio.
 */
function findAudioPageInSpread(flipbookPageIndex) {
  const leftIdx = flipbookPageIndex;
  const rightIdx = flipbookPageIndex + 1;

  const leftStoryIdx = getStoryPageIndex(leftIdx);
  const rightStoryIdx = getStoryPageIndex(rightIdx);

  if (getPageAudioUrl(leftStoryIdx)) return leftIdx;
  if (getPageAudioUrl(rightStoryIdx)) return rightIdx;
  return -1;
}

/**
 * Update play button enabled/disabled state based on current page audio availability.
 */
function updatePlayButtonForPage(flipbookPageIndex) {
  const playPauseBtn = document.getElementById('playPauseBtn');
  if (!playPauseBtn) return;

  // During recording, play is always disabled
  if (isRecording) {
    playPauseBtn.disabled = true;
    return;
  }

  // In record mode, play is only enabled if there's a child recording for this page
  if (currentReadingMode === 'record') {
    let rec = pageRecordings.get(flipbookPageIndex);
    // In double-page mode, also check the right page
    if ((!rec || rec.status !== 'ready') && isDoublePageMode()) {
      rec = pageRecordings.get(flipbookPageIndex + 1);
    }
    playPauseBtn.disabled = !(rec && rec.status === 'ready');
    return;
  }

  // In listen or read-listen mode, check if publisher audio exists
  if (currentReadingMode === 'listen' || currentReadingMode === 'read-listen') {
    const storyIdx = getStoryPageIndex(flipbookPageIndex);
    const url = getPageAudioUrl(storyIdx);

    // Always check the right page too (covers double-page mode and avoids
    // timing issues where --landscape class isn't set yet after resize)
    if (!url) {
      const rightStoryIdx = getStoryPageIndex(flipbookPageIndex + 1);
      const rightUrl = getPageAudioUrl(rightStoryIdx);
      playPauseBtn.disabled = !rightUrl;
      return;
    }

    playPauseBtn.disabled = !url;
    return;
  }

  // In listen-adult mode, check if adult recording exists for this page
  if (currentReadingMode === 'listen-adult') {
    const storyIdx = getStoryPageIndex(flipbookPageIndex);
    const adultUrl = getAdultRecordingUrl(storyIdx + 1); // 1-based page number
    if (!adultUrl && isDoublePageMode()) {
      const rightStoryIdx = getStoryPageIndex(flipbookPageIndex + 1);
      const rightAdultUrl = getAdultRecordingUrl(rightStoryIdx + 1);
      playPauseBtn.disabled = !rightAdultUrl;
      return;
    }
    playPauseBtn.disabled = !adultUrl;
    return;
  }

  // Default: disabled
  playPauseBtn.disabled = true;
}

/**
 * Toggle audio playback for the current page.
 * Uses real Audio element when publisher audio URL is available,
 * falls back to mock audio for testing when no URL exists.
 */
function toggleAudioPlayback() {
  const playIcon = document.getElementById('playIcon');
  const pauseIcon = document.getElementById('pauseIcon');
  const playPauseBtn = document.getElementById('playPauseBtn');

  // No playback until user selects a mode from the glossary page
  if (!currentReadingMode || currentReadingMode === 'read') return;

  // Stop star bounce when user interacts with audio
  stopStarBounce();

  if (isAudioPlaying) {
    // === PAUSE ===
    if (audioElement && audioElement.pause) {
      audioElement.pause();
    }
    isAudioPlaying = false;
    playIcon.classList.remove('audio-icon--hidden');
    pauseIcon.classList.add('audio-icon--hidden');
    playPauseBtn.setAttribute('aria-label', 'Play audio');
    // Re-enable record button on pause (only in record mode)
    const recordBtnPause = document.getElementById('recordBtn');
    if (recordBtnPause) recordBtnPause.disabled = currentReadingMode !== 'record';
    return;
  }

  // === PLAY ===
  const currentFlipIndex = pageFlipInstance ? pageFlipInstance.getCurrentPageIndex() : 0;
  const storyIdx = getStoryPageIndex(currentFlipIndex);

  // Disable record button during playback (Req 3.3)
  const recordBtn = document.getElementById('recordBtn');
  if (recordBtn) recordBtn.disabled = true;

  // In record mode, play the child's recording for this page (Req 8.1, 8.2)
  if (currentReadingMode === 'record') {
    let rec = pageRecordings.get(currentFlipIndex);
    let recFlipIndex = currentFlipIndex;
    // In double-page mode, if left page has no recording, check the right page
    if ((!rec || rec.status !== 'ready') && isDoublePageMode()) {
      const rightRec = pageRecordings.get(currentFlipIndex + 1);
      if (rightRec && rightRec.status === 'ready') {
        rec = rightRec;
        recFlipIndex = currentFlipIndex + 1;
      }
    }
    if (rec && rec.status === 'ready' && rec.url) {
      audioElement = new Audio(rec.url);
      audioElement._kassieFlipIndex = recFlipIndex;
      playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
      return;
    }
    // No recording available — do nothing
    if (recordBtn) recordBtn.disabled = currentReadingMode !== 'record';
    return;
  }

  // In listen-adult mode, play adult family recording for this page
  if (currentReadingMode === 'listen-adult') {
    const adultRec = getAdultRecordingUrl(storyIdx + 1); // storyIdx is 0-based, recordings use 1-based page
    const adultFlipIndex = currentFlipIndex;

    // In double-page mode, if left page has no adult audio, check right page
    if (!adultRec && isDoublePageMode()) {
      const rightFlipIndex = currentFlipIndex + 1;
      const rightStoryIdx = getStoryPageIndex(rightFlipIndex);
      const rightAdultRec = getAdultRecordingUrl(rightStoryIdx + 1);
      if (rightAdultRec) {
        audioElement = new Audio(rightAdultRec);
        audioElement._kassieFlipIndex = rightFlipIndex;
        playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
        return;
      }
    }

    if (adultRec) {
      audioElement = new Audio(adultRec);
      audioElement._kassieFlipIndex = adultFlipIndex;
      playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
      return;
    }

    // No adult recording for this page — disable
    if (recordBtn) recordBtn.disabled = currentReadingMode !== 'record';
    return;
  }

  // In listen / read-listen mode, play publisher audio
  let audioUrl = getPageAudioUrl(storyIdx);
  let audioFlipIndex = currentFlipIndex;

  // In double-page mode, if the left page has no audio, check the right page
  if (!audioUrl && isDoublePageMode()) {
    const rightFlipIndex = currentFlipIndex + 1;
    const rightStoryIdx = getStoryPageIndex(rightFlipIndex);
    const rightUrl = getPageAudioUrl(rightStoryIdx);
    if (rightUrl) {
      audioUrl = rightUrl;
      audioFlipIndex = rightFlipIndex;
      console.log('🔊 Audio found on right page of spread (page', rightStoryIdx + 1, ')');
    }
  }

  if (audioUrl) {
    // Real publisher audio
    const cached = pageAudioCache.get(audioFlipIndex);
    if (cached && cached.status === 'ready') {
      audioElement = cached.audio;
      audioElement.currentTime = audioElement.currentTime || 0; // Resume from where paused
      audioElement._kassieFlipIndex = audioFlipIndex;
      playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
    } else {
      // Show loading state
      playPauseBtn.disabled = true;
      playPauseBtn.setAttribute('aria-label', 'Loading audio...');
      console.log('🔊 Loading audio for page', getStoryPageIndex(audioFlipIndex) + 1);

      loadPageAudio(audioFlipIndex).then((entry) => {
        playPauseBtn.disabled = false;
        if (entry && entry.status === 'ready') {
          audioElement = entry.audio;
          audioElement._kassieFlipIndex = audioFlipIndex;
          playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
        } else {
          console.error('❌ Audio failed to load for page', getStoryPageIndex(audioFlipIndex) + 1);
          playPauseBtn.setAttribute('aria-label', 'Audio unavailable');
        }
      });
    }
  } else {
    // No publisher audio — use mock for testing
    if (!audioElement || !audioElement._isMock) {
      console.log('🔊 No audio URL — creating mock audio for testing');
      audioElement = createMockAudio();
    }
    audioElement.play();
    isAudioPlaying = true;
    playIcon.classList.add('audio-icon--hidden');
    pauseIcon.classList.remove('audio-icon--hidden');
    playPauseBtn.setAttribute('aria-label', 'Pause audio');
  }
}

/**
 * Play a real Audio element and bind progress/end events.
 */
let audioProgressRAF = null;

function playRealAudio(audio, playIcon, pauseIcon, playPauseBtn) {
  // Ensure iOS audio is unlocked (resume AudioContext if suspended)
  ensureAudioUnlocked();

  // Apply stored volume
  if (typeof window._kassieVolume === 'number') {
    audio.volume = window._kassieVolume;
  }

  // Remove previous event listeners to avoid duplicates
  audio.onended = null;
  audio.onerror = null;

  audio.onended = () => {
    onAudioEnded();
    audioProgressRAF = null;
  };

  audio.onerror = (e) => {
    console.error('❌ Audio playback error:', e);
    isAudioPlaying = false;
    playIcon.classList.remove('audio-icon--hidden');
    pauseIcon.classList.add('audio-icon--hidden');
    audioProgressRAF = null;
    // Re-enable record button on error (only in record mode)
    const recordBtn = document.getElementById('recordBtn');
    if (recordBtn) recordBtn.disabled = currentReadingMode !== 'record';
  };

  // Set playing state IMMEDIATELY so pause clicks work even before play() resolves.
  // If play() fails, the .catch() resets the state.
  isAudioPlaying = true;
  playIcon.classList.add('audio-icon--hidden');
  pauseIcon.classList.remove('audio-icon--hidden');
  playPauseBtn.setAttribute('aria-label', 'Pause audio');

  audio
    .play()
    .then(() => {
      // Start progress tracking
      function trackProgress() {
        if (!isAudioPlaying) return;
        updateAudioProgress(audio.currentTime, audio.duration);
        audioProgressRAF = requestAnimationFrame(trackProgress);
      }
      trackProgress();
    })
    .catch((err) => {
      console.warn('⚠️ Audio play() rejected (likely iOS autoplay restriction):', err.message);
      // Reset state since play failed
      isAudioPlaying = false;
      playIcon.classList.remove('audio-icon--hidden');
      pauseIcon.classList.add('audio-icon--hidden');
      playPauseBtn.setAttribute('aria-label', 'Play audio');

      // Show "tap to play" hint and retry on next user interaction
      showTapToPlayHint();
      const retryHandler = () => {
        ensureAudioUnlocked();
        hideTapToPlayHint();
        audio.play().then(() => {
          isAudioPlaying = true;
          playIcon.classList.add('audio-icon--hidden');
          pauseIcon.classList.remove('audio-icon--hidden');
          playPauseBtn.setAttribute('aria-label', 'Pause audio');
          function trackProgress() {
            if (!isAudioPlaying) return;
            updateAudioProgress(audio.currentTime, audio.duration);
            audioProgressRAF = requestAnimationFrame(trackProgress);
          }
          trackProgress();
        }).catch(() => { /* give up silently */ });
      };
      document.addEventListener('click', retryHandler, { once: true });
      document.addEventListener('touchstart', retryHandler, { once: true });
    });
}

/**
 * Create a mock audio object for testing when no real audio URL exists.
 */
function createMockAudio() {
  const mock = {
    _isMock: true,
    play: () => {
      isAudioPlaying = true;
      console.log('▶️ Mock audio playing');
      startMockAudioProgress();
    },
    pause: () => {
      isAudioPlaying = false;
      console.log('⏸️ Mock audio paused');
      stopMockAudioProgress();
    },
    currentTime: 0,
    duration: 120,
  };
  return mock;
}

// Mock audio progress for testing
let mockAudioInterval = null;
let mockCurrentTime = 0;

function startMockAudioProgress() {
  mockCurrentTime = 0;
  mockAudioInterval = setInterval(() => {
    mockCurrentTime += 0.1;
    const duration = 120; // 2 minutes

    if (mockCurrentTime >= duration) {
      mockCurrentTime = duration;
      stopMockAudioProgress();
      onAudioEnded();
      return;
    }

    updateAudioProgress(mockCurrentTime, duration);
  }, 100);
}

function stopMockAudioProgress() {
  if (mockAudioInterval) {
    clearInterval(mockAudioInterval);
    mockAudioInterval = null;
  }
}

/**
 * Show a subtle "tap to play" hint when iOS blocks auto-play.
 */
function showTapToPlayHint() {
  hideTapToPlayHint();
  const hint = document.createElement('div');
  hint.id = 'tapToPlayHint';
  hint.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.7);color:#fff;padding:8px 16px;border-radius:8px;font-size:13px;z-index:9999;pointer-events:none;';
  hint.textContent = t('child.audio.tapToPlay') || 'Tap to play';
  document.body.appendChild(hint);
}

/**
 * Hide the "tap to play" hint.
 */
function hideTapToPlayHint() {
  const existing = document.getElementById('tapToPlayHint');
  if (existing) existing.remove();
}

/* ──────────────────────────────────────────────────────────────────────────
 * Recording coach state machine (#146)
 *
 * All narration guidance goes through one overlaid toast. Previous versions
 * inserted separate hint elements as siblings of .audio-controls, which sit in
 * the flex flow and so pushed the player up and squeezed the book.
 * ────────────────────────────────────────────────────────────────────────── */

/** Timer that returns the coach to its resting state after a confirmation. */
let coachRestTimer = null;

/** Flip index seen on the previous onFlip, used to derive flip direction. */
let previousFlipIndex = null;

/**
 * Whether the current profile is a grown-up recording for the children.
 * Covers both the in-app entry (adult profile opens a book) and the legacy
 * admin deep link.
 *
 * @returns {boolean}
 */
function isAdultNarrator() {
  return !!(currentProfile?.isAdultRecorder || currentProfile?.type === 'adult');
}

/**
 * Story page numbers that have audio, from any source: confirmed uploads,
 * in-flight saves, and locally queued pages.
 *
 * @returns {number[]} 1-based story page numbers
 */
function recordedStoryPages() {
  const pages = [];
  pageRecordings.forEach((rec) => {
    if (!rec) return;
    if (rec.status === 'ready' || rec.status === 'saving') {
      if (typeof rec.storyPage === 'number') pages.push(rec.storyPage);
    }
  });
  return pages;
}

/**
 * Mark whether the reader is showing a story page, as opposed to one of the
 * two prepended pages (the cover and the index/glossary).
 *
 * Lets CSS treat them differently — those two pages have their own layout and
 * do not want the story-page spacing.
 *
 * @param {number} pageIndex - Flipbook index
 * @returns {void}
 */
function markOnStoryPage(pageIndex) {
  const storyContent = document.querySelector('.story-content');
  if (!storyContent) return;
  storyContent.dataset.onStoryPage = pageIndex >= PREPENDED_PAGE_COUNT ? 'true' : 'false';
}

/**
 * The page a reader would carry on recording from.
 *
 * Uses the highest recorded page rather than the first gap, because a
 * deliberately skipped page (an illustration-only spread) should not drag the
 * reader backwards. Same rule the adult resume uses.
 *
 * @returns {number|null} 1-based story page, or null when there is nothing to
 *   resume to — either nothing recorded yet, or the whole story is done.
 */
function computeResumePage() {
  const total = currentStoryData?.pages?.length || 0;
  const recorded = recordedStoryPages();
  if (!total || recorded.length === 0) return null;

  const next = Math.max(...recorded) + 1;
  return next > total ? null : next;
}

/**
 * Position label for the coach.
 *
 * Mirrors the page number printed in the book's own footer
 * (`<page> / <total>` from createPageElement) so the toast and the page agree,
 * and so the label keeps up when the narrator pages back and forth.
 *
 * It deliberately does not count recordings: skipping pages is a legitimate
 * choice (illustration-only pages), so a "x of y recorded" tally read as an
 * unmet target and was wrong whenever an earlier recording was already loaded.
 *
 * @returns {string|null}
 */
function progressLabel() {
  const total = currentStoryData?.pages?.length || 0;
  if (!total) return null;
  const page = currentStoryPageNumber();
  if (page === null || page > total) return null;
  return t('child.adultRecording.progress', { page, total }) || `Bladsy ${page} van ${total}`;
}

/**
 * The same position, short enough for the corner circle on the toast.
 *
 * @returns {string|null} e.g. "2/7"
 */
function pageBadge() {
  const total = currentStoryData?.pages?.length || 0;
  const page = currentStoryPageNumber();
  if (!total || page === null || page > total) return null;
  return `${page}/${total}`;
}

/**
 * Story page number currently under the record button.
 * @returns {number|null}
 */
function currentStoryPageNumber() {
  if (!pageFlipInstance) return null;
  const storyPage = pageFlipInstance.getCurrentPageIndex() - PREPENDED_PAGE_COUNT + 1;
  return storyPage >= 1 ? storyPage : null;
}

/**
 * Render a coach state.
 *
 * @param {'ready'|'rerecord'|'recording'|'saved'|'stopped'|'resume'|'allRecorded'|'sending'|'notAllSent'|'error'} state
 * @param {Object} [data]
 * @returns {void}
 */
function setCoachState(state, data = {}) {
  if (currentReadingMode !== 'record') return;

  // Once the session has been confirmed complete, the coach must never reappear.
  // The narrator may swipe back to review their work, but the "press record" /
  // "send recording" prompts make no sense after the completion toast.
  if (completionSequenceDone) return;

  // Pages upload as they are recorded, so "send" is a finish-and-confirm action
  // rather than the only way out. On every page it was just noise taking up
  // height. Offer it where finishing is actually plausible: the last page, or
  // once the narrator has paused.
  const total = currentStoryData?.pages?.length || 0;
  const page = currentStoryPageNumber();
  const onLastPage = total > 0 && page !== null && page >= total;
  // "Stuur die opname" button removed — uploads happen automatically now.
  // Kept the variable so the structure is easy to restore if needed.
  const offerSend = false;

  const base = {
    badge: pageBadge(),
    badgeTitle: progressLabel(),
    action: offerSend
      ? {
        label: t('child.adultRecording.sendRecording') || 'Stuur die opname',
        onClick: sendRecordingAndFinish,
      }
      : null,
  };

  switch (state) {
    case 'ready': {
      const page = currentStoryPageNumber();
      if (page !== null && recordedStoryPages().includes(page)) {
        setCoachState('rerecord');
        return;
      }

      // A child on the last page should not see "press record" — the session
      // will finish the moment they record and stop, so the ready prompt is
      // misleading. The celebration toast will take over.
      // Same for an adult: the send was already offered (or has already run),
      // and showing "Druk die {record}" + "Stuur die opname" over the
      // completion toast is wrong.
      if (onLastPage && recordedStoryPages().length > 0) {
        hideCoach();
        return;
      }

      showCoach({
        ...base,
        message:
          sessionRecordCount === 0
            ? t('child.adultRecording.pressToStart') || 'Druk die {record} om te begin opneem'
            : t('child.adultRecording.pressWhenReady') || 'Druk die {record} wanneer jy reg is',
      });
      break;
    }

    case 'rerecord': {
      const rerecordPage = currentStoryPageNumber();
      showCoach({
        ...base,
        message:
          t('child.adultRecording.alreadyRecorded', { page: rerecordPage }) ||
          `Bladsy ${rerecordPage} is al opgeneem, druk {record} om weer op te neem`,
      });
      break;
    }

    case 'recording':
      showCoach({
        ...base,
        action: null, // Sending mid-recording would be ambiguous
        message:
          t('child.adultRecording.recordingNow') ||
          'Druk die {star} om om te blaai, of die {stop} knoppie om tydelik op te hou',
      });
      break;

    case 'saved':
      showCoach({
        ...base,
        tone: 'success',
        message:
          t('child.adultRecording.pageSaved', { page: data.page }) ||
          `{tick} Bladsy ${data.page} opgeneem`,
      });
      // Return to the resting state once the confirmation has been seen
      clearTimeout(coachRestTimer);
      coachRestTimer = setTimeout(() => {
        if (!isRecording && currentReadingMode === 'record') setCoachState('ready');
      }, 2000);
      break;

    case 'stopped':
      showCoach({
        ...base,
        message:
          t('child.adultRecording.recordingStopped') ||
          'Opname het nou opgehou. Begin weer deur {record} te druk, en {star} om om te blaai.',
      });
      break;

    case 'resume':
      showCoach({
        ...base,
        message:
          t('child.adultRecording.resume', { last: data.last, next: data.next }) ||
          `Jy was laas by Bladsy ${data.last} — ons gaan voort by Bladsy ${data.next}.`,
      });
      break;

    case 'allRecorded':
      showCoach({
        ...base,
        message:
          t('child.adultRecording.allRecorded', { total: currentStoryData?.pages?.length || 0 }) ||
          'Al die bladsye is opgeneem. Druk {record} om enige bladsy weer op te neem.',
      });
      break;

    case 'sending':
      showCoach({
        ...base,
        action: null,
        message:
          t('child.adultRecording.sending') ||
          "Ons stoor nog 'n paar bladsye — bly net 'n oomblik.",
      });
      break;

    case 'notAllSent':
      showCoach({
        ...base,
        tone: 'error',
        message: t('child.adultRecording.notAllSent') || 'Party bladsye is nog nie gestuur nie.',
      });
      break;

    case 'error':
      showCoach({
        ...base,
        tone: 'error',
        message:
          data.message ||
          t('child.adultRecording.uploadFailed') ||
          'Ons kon nie die opname stoor nie. Druk {record} om weer op te neem.',
      });
      break;

    default:
      break;
  }
}

/**
 * Mark a page as recorded the moment the user stops, before the blob has been
 * normalised or uploaded.
 *
 * Previously the confirmation and the progress counter both waited for the
 * async onstop handler, so "Bladsy X opgeneem" arrived seconds late (often
 * after the page had already advanced) and the counter read "0 / 13".
 *
 * Both stop paths land here — the manual stop in toggleRecording and the
 * page-turn stop via handleRecordModeFlip — so this is where each reader gets
 * its confirmation: the coach for an adult, a toast for a child.
 *
 * @param {number|null} flipIdx - Flipbook index that was being recorded
 * @returns {void}
 */
function markPageRecordedOptimistic(flipIdx) {
  if (flipIdx === null || flipIdx === undefined) return;

  const storyPage = flipIdx - PREPENDED_PAGE_COUNT + 1;
  if (storyPage < 1) return;

  const existing = pageRecordings.get(flipIdx);
  pageRecordings.set(flipIdx, { ...(existing || {}), status: 'saving', storyPage });

  setCoachState('saved', { page: storyPage });
  updatePageRecordedBadges();
}

/**
 * Revert an optimistic mark when the recording turned out to be unusable.
 *
 * @param {number|null} flipIdx
 * @param {string} [message] - Coach message explaining why
 * @returns {void}
 */
function revertOptimisticMark(flipIdx, message) {
  if (flipIdx !== null && flipIdx !== undefined) {
    const rec = pageRecordings.get(flipIdx);
    if (rec && rec.status === 'saving' && !rec.url) {
      // No previous successful recording — remove entirely
      pageRecordings.delete(flipIdx);
    } else if (rec && (rec.status === 'saving' || rec.status === 'in-progress') && rec.url) {
      // Had a previous successful recording — restore to 'ready' (don't lose the old URL)
      rec.status = 'ready';
    }
  }
  clearTimeout(coachRestTimer);
  setCoachState('error', { message });
  updatePageRecordedBadges();
}

/**
 * Finish the session: celebrate, then confirm everything reached the server.
 *
 * Uploads already happen per page as they are recorded, so this is a "finish
 * and confirm" action rather than the only upload path. If the narrator never
 * triggers it, the work is already on its way.
 *
 * The celebration runs first and the toast then tracks the send, so the narrator
 * sees progress rather than a spinner-free pause. It ends on a single
 * confirmation with an OK button. Nothing navigates on its own: the narrator
 * leaves via that button or a nav icon, and anything else they do dismisses it.
 *
 * @returns {Promise<void>}
 */
async function sendRecordingAndFinish() {
  const pageStatuses = {};
  pageRecordings.forEach((rec, idx) => {
    pageStatuses[`flip${idx}→p${rec.storyPage}`] = rec.status + (rec.url ? ' ✓url' : ' (no url)');
  });
  // Count all pages that have any recording activity (ready, saving, or in-progress)
  let coveredCount = 0;
  pageRecordings.forEach((rec) => {
    if (rec && ['ready', 'saving', 'in-progress'].includes(rec.status)) coveredCount++;
  });
  recDbg.trace('sendRecordingAndFinish', { recordedPages: recordedStoryPages().length, coveredCount, completionToast: !!completionToast, completionSequenceDone, pageStatuses });
  if (coveredCount === 0) { recDbg.warn('sendRecordingAndFinish aborted — no recorded/in-progress pages'); return; }

  if (completionToast || completionSequenceDone) { recDbg.warn('sendRecordingAndFinish aborted — already showing completion', { completionToast: !!completionToast, completionSequenceDone }); return; }

  recDbg.ok('Calling showRecordingComplete()');
  showRecordingComplete();

  await wait(SEQUENCE_START_MS);
  hideCoach();

  // ── Child profile: no send step — their recordings upload per-page anyway ──
  if (!isAdultNarrator()) {
    // If iOS wrapped the flipbook back to the cover, flip to index/glossary
    // page so the toast appears in a visible, sensible context.
    if (pageFlipInstance) {
      const currentIdx = pageFlipInstance.getCurrentPageIndex();
      if (currentIdx === 0) {
        pageFlipInstance.flip(1);
      }
    }

    const confirmation = showToast(
      t('child.recording.childRecordingDone') || 'Jou storie is opgeneem',
      'success',
      0,
      { stack: true, persist: true }
    );
    completionToast = confirmation;
    completionSequenceDone = true;

    appendToToast(confirmation, {
      buttonLabel: t('child.recording.okay') || 'Okay',
      onButtonClick: () => {
        clearCompletionToast();
        router.navigate('library', currentProfile);
      },
    });

    watchForCompletionToastDismissal();
    return;
  }

  // ── Adult profile: show the confirmation immediately, optimistic wording ──
  //
  // If iOS wrapped the flipbook back to the cover, flip to index/glossary page
  // so the toast appears in a visible context.
  if (pageFlipInstance) {
    const currentIdx = pageFlipInstance.getCurrentPageIndex();
    if (currentIdx === 0) {
      pageFlipInstance.flip(1);
    }
  }

  //
  // Previously this toast waited on `await hasPendingRecordings()`, which goes
  // through IndexedDB. iOS Safari's indexedDB.open() can hang indefinitely —
  // fires neither onsuccess nor onerror, worst in installed-PWA mode — with no
  // timeout anywhere in that path, so the toast could simply never appear
  // (see REPORT_iOS_TOAST_LAYOUT_2026-08-09.md). recordingStore.js's getDB()
  // now has its own 800ms timeout, but the toast's *existence* should not
  // depend on that call succeeding at all, only its exact wording should.
  recDbg.ok('Adult narrator path — showing confirmation toast');
  const confirmation = showToast(
    t('child.recording.adultSentDone') || 'Jou storie is opgeneem en gestuur!',
    'success',
    0,
    { stack: true, persist: true }
  );
  completionToast = confirmation;
  completionSequenceDone = true;

  appendToToast(confirmation, {
    buttonLabel: t('child.recording.okay') || 'Okay',
    onButtonClick: () => {
      clearCompletionToast();
      router.navigate('library', currentProfile);
    },
  });

  // Anything else the narrator does takes the toast away
  watchForCompletionToastDismissal();

  // Background check — may upgrade the wording to "still sending" in place.
  // Never gates the toast above, and never re-shows/re-animates/repositions it.
  (async () => {
    try {
      const { hasPendingRecordings, flushPendingRecordings } = await import(
        '../services/recordingStore.js'
      );

      const hasPending = await hasPendingRecordings();

      // Two guards, both required, before mutating a toast already on screen:
      //   - isConnected: the narrator may have already tapped OK, or turned a
      //     page, either of which removes the toast via clearCompletionToast()
      //     or watchForCompletionToastDismissal(). Writing into a detached
      //     node is harmless but pointless.
      //   - === completionToast: a narrator who finishes, dismisses, and
      //     immediately starts and finishes a second session would otherwise
      //     have this stale background check mutate the *new* toast.
      if (hasPending && confirmation.isConnected && confirmation === completionToast) {
        const line = confirmation.querySelector('.kassie-toast__line');
        if (line) {
          line.textContent =
            t('child.recording.adultSending') ||
            'Ons is besig om jou opname te stuur na die kleinspan!';
        }

        // Fire the flush but don't wait for it — the toast already told the
        // narrator the upload is still in progress and they can leave via OK.
        flushPendingRecordings().then(({ failed }) => {
          if (failed > 0) console.warn(`⚠️ ${failed} page(s) still pending after flush`);
          updatePageRecordedBadges();
        });
      }
    } catch (err) {
      console.warn('Could not check/flush pending recordings:', err.message);
    }
  })();
}

/**
 * Shared record-mode page-turn handling.
 *
 * Called from both onFlip handlers. They previously diverged: the instance
 * created by reinitializePageFlip (active on desktop double-page, which is the
 * default entry path now that recording starts from the index card) never
 * stopped the recorder, so audio spanning two pages was written onto the first.
 *
 * @param {number} pageIndex - New flipbook index
 * @returns {void}
 */
function handleRecordModeFlip(pageIndex) {
  const previous = previousFlipIndex;
  previousFlipIndex = pageIndex;
  recDbg.trace('handleRecordModeFlip', { pageIndex, previous, mode: currentReadingMode, isRecording });

  // Flag whether we are past the prepended pages (cover, index/glossary), so
  // CSS can treat story pages differently from those two.
  markOnStoryPage(pageIndex);

  // Turning a page moves on from the confirmation, so it should not linger.
  // BUT: skip if the completion sequence just fired — the programmatic flip
  // to the index page (from sendRecordingAndFinish) should NOT dismiss the
  // toast it just showed. Only user-initiated page turns should clear it.
  if (!completionSequenceDone) {
    clearCompletionToast();
  }

  if (currentReadingMode !== 'record') {
    // When the book flips past the last story page (to back cover or iOS
    // wrap to page 0) while recording is still technically active (mode
    // went null but mediaRecorder hasn't stopped yet), auto-stop the
    // recording and trigger finish. The narrator already recorded the last
    // page — what's capturing now is dead air on the cover.
    if (isRecording && !completionSequenceDone) {
      const totalFlipPages = pageFlipInstance ? pageFlipInstance.getPageCount() : 0;
      const isOnBackCoverOrWrap = pageIndex >= totalFlipPages - 1 || pageIndex === 0;
      if (isOnBackCoverOrWrap) {
        recDbg.ok('Mode null + still recording on cover/wrap → auto-stopping and finishing');
        // Stop the recorder, then finish
        toggleRecording(); // this stops recording and schedules autoAdvanceAfterRecording
        return;
      }
    }
    currentRecordingPageIndex = pageIndex;
    updateRecordingOverlays();
    return;
  }

  const movingBackward = previous !== null && pageIndex < previous;

  if (!isRecording) {
    currentRecordingPageIndex = pageIndex;
    updateRecordingOverlays();
    updatePageRecordedBadges();

    // If we've landed on the back cover after recording, finish the session
    const totalFlipPages = pageFlipInstance ? pageFlipInstance.getPageCount() : 0;
    const isOnBackCover = totalFlipPages > 0 && pageIndex >= totalFlipPages - 1;
    recDbg.log(`handleRecordModeFlip (not recording): pageIndex=${pageIndex}, totalFlip=${totalFlipPages}, isOnBackCover=${isOnBackCover}, recordedPages=${recordedStoryPages().length}`);
    if (isOnBackCover && recordedStoryPages().length > 0) {
      recDbg.ok('Back cover reached (not recording) → sendRecordingAndFinish()');
      sendRecordingAndFinish();
    } else {
      setCoachState('ready');
    }
    return;
  }

  const recordedIdx = currentRecordingPageIndex;

  stopRecordingForPageTurn().then(() => {
    markPageRecordedOptimistic(recordedIdx);

    const totalFlipPages = pageFlipInstance ? pageFlipInstance.getPageCount() : 0;
    const isOnBackCover = totalFlipPages > 0 && pageIndex >= totalFlipPages - 1;
    const lastStoryFlipIndex = PREPENDED_PAGE_COUNT + (currentStoryData?.pages?.length || 0) - 1;
    const isPastLastStory = pageIndex > lastStoryFlipIndex;

    if (isOnBackCover || isPastLastStory) {
      console.log('Recording stopped - reached end of story');
      sendRecordingAndFinish();
      return;
    }

    // Turning back is a deliberate move to revisit a page. Auto-restarting
    // there would silently record over existing audio, so stop and wait.
    if (movingBackward) {
      console.log('Flipped backward - recording stopped, waiting for the narrator');
      setCoachState('stopped');
      return;
    }

    // Forward: carry on recording the new page
    setTimeout(() => {
      if (!isRecording) {
        toggleRecording();
        console.log('Auto-started recording on new page after page turn');
      }
    }, 2000);
  });
}

/**
 * Jump to where the narrator left off and explain the jump.
 *
 * Resume position is derived from the recordings themselves, so nothing extra
 * is persisted. Uses the highest recorded page rather than the first gap,
 * because a deliberately skipped page should not drag them back.
 *
 * @returns {void}
 */
function resumeRecordingSession() {
  // Both readers can resume. Whether they should is decided by the callers:
  // adults automatically, children only via "Remember my story". For a child
  // this performs the page turn only — the badge and coach calls below already
  // no-op for them, which is why the jump is silent.
  if (!pageFlipInstance) return;

  const total = currentStoryData?.pages?.length || 0;
  const recorded = recordedStoryPages();
  updatePageRecordedBadges();

  if (recorded.length === 0) {
    setCoachState('ready');
    return;
  }

  const last = Math.max(...recorded);
  const next = last + 1;

  if (recorded.length >= total || next > total) {
    // For children: show the "your story is recorded" toast and let them
    // decide what to do (re-record, listen, or go back to library).
    // For adults: show the coach allRecorded state with re-record guidance.
    if (!isAdultNarrator()) {
      sendRecordingAndFinish();
    } else {
      setCoachState('allRecorded');
    }
    return;
  }

  pageFlipInstance.flip(next + PREPENDED_PAGE_COUNT - 1);
  setCoachState('resume', { last, next });
}

/**
 * Stamp a tick in the top-right corner of each recorded page.
 *
 * Two states, WhatsApp-style:
 *   grey  - audio is on this device but has not reached the server
 *   green - confirmed in Firestore
 *
 * Badges attach to the .page elements (same as .recording-overlay) so they
 * travel with the flip animation, and are pointer-events: none so they never
 * interfere with StPageFlip's drag handling.
 *
 * @returns {void}
 */
function updatePageRecordedBadges() {
  const flipBookContainer = document.getElementById('flipBook');
  if (!flipBookContainer) return;

  flipBookContainer.querySelectorAll('.page').forEach((pageEl, flipIdx) => {
    const existing = pageEl.querySelector('.page-recorded-badge');
    const rec = pageRecordings.get(flipIdx);

    if (!rec || (rec.status !== 'ready' && rec.status !== 'saving')) {
      if (existing) existing.remove();
      return;
    }

    // A remote URL means the server has it; a blob URL or no URL means local only
    const isUploaded = rec.status === 'ready' && !!rec.url && !rec.url.startsWith('blob:');
    const label = isUploaded
      ? t('child.adultRecording.badgeSent') || 'Gestuur'
      : t('child.adultRecording.badgeLocal') || 'Op hierdie toestel';

    const badge = existing || document.createElement('img');
    badge.src = '/svg/buttons/Tick.svg';
    badge.className = isUploaded
      ? 'page-recorded-badge'
      : 'page-recorded-badge page-recorded-badge--local';
    badge.alt = label;
    badge.title = label;
    if (!existing) pageEl.appendChild(badge);
  });
}

function updateAudioProgress(currentTime, duration) {
  const progressFill = document.getElementById('audioProgressFill');
  const currentTimeEl = document.getElementById('audioCurrentTime');
  const durationEl = document.getElementById('audioDuration');

  if (progressFill) {
    const percentage = (currentTime / duration) * 100;
    progressFill.style.width = `${percentage}%`;
  }

  if (currentTimeEl) {
    currentTimeEl.textContent = formatTime(currentTime);
  }

  if (durationEl) {
    durationEl.textContent = formatTime(duration);
  }
}

/**
 * Toggle recording state
 * Uses MediaRecorder API to capture audio from microphone.
 * Creates a blob URL for playback when recording stops.
 */
let isRecording = false;
let recordFlashInterval = null;
let mediaRecorder = null;
let recordedChunks = [];
let micStream = null;
let lastRecordingUrl = null;
let lastRecordingStatus = 'none';
const recordingAutoSaveTimeout = null;
let recordingStartTime = null;

// Silence detection state
let audioContext = null;
let analyserNode = null;
let silenceDetectionRAF = null;
let voiceLevelSamples = [];
let averageVoiceLevel = 0;
let calibrationComplete = false;
let silenceStartTime = null;
let silenceConfirmationShown = false;
let silenceAutoStopTimeout = null;

const CALIBRATION_DURATION = 2000; // 2 seconds to calibrate voice level
const SILENCE_THRESHOLD_RATIO = 0.3; // Below 30% of average = silence
const SILENCE_DURATION_MS = 12000; // 12 seconds of silence before prompt
const AUTO_STOP_AFTER_PROMPT_MS = 5000; // 5 more seconds after prompt = auto-stop

// Per-page recording map: pageIndex → { url, status, storyPage }
// storyPage is the 1-based story page number (pageIndex - PREPENDED_PAGE_COUNT + 1)
const pageRecordings = new Map();

/**
 * Clear the per-page recording map and release any local blob URLs.
 *
 * This map is module-level, so it must be reset whenever a different story or
 * a different profile opens the reader — otherwise one child's recordings stay
 * playable for the next profile, and page 1 of story A leaks into story B.
 */
function resetPageRecordings() {
  pageRecordings.forEach((rec) => {
    if (rec?.url && rec.url.startsWith('blob:')) {
      URL.revokeObjectURL(rec.url);
    }
  });
  pageRecordings.clear();
}

/**
 * Recording page overlay for double-page spread mode.
 * Shows a semi-transparent white overlay on the page NOT being recorded,
 * so the user knows which page the microphone is capturing for.
 */
function updateRecordingOverlays() {
  // Remove all existing overlays first
  document.querySelectorAll('.recording-overlay').forEach((el) => el.remove());

  if (!isRecording || currentRecordingPageIndex === null || !pageFlipInstance) return;

  // Check if we're in double-page mode by looking at StPageFlip orientation
  // In portrait/single mode there's only one visible page — no overlay needed
  const flipBookContainer = document.getElementById('flipBook');
  if (!flipBookContainer) return;

  const stfWrapper = flipBookContainer.querySelector('.stf__wrapper');
  if (!stfWrapper) return;

  // StPageFlip uses class "--portrait" for single and "--landscape" for double
  const isDoublePage = stfWrapper.classList.contains('--landscape');
  if (!isDoublePage) return;

  // In double-page mode, the visible pages are the current index (left) and current+1 (right)
  const currentFlipIndex = pageFlipInstance.getCurrentPageIndex();
  const leftPageIndex = currentFlipIndex;
  const rightPageIndex = currentFlipIndex + 1;

  // Get all page elements inside the flipbook
  const allPages = flipBookContainer.querySelectorAll('.page');

  // Add overlay to the page that is NOT being recorded
  [leftPageIndex, rightPageIndex].forEach((pageIdx) => {
    if (pageIdx !== currentRecordingPageIndex && pageIdx >= 0 && pageIdx < allPages.length) {
      const page = allPages[pageIdx];
      // Only add overlay if this page doesn't already have one
      if (!page.querySelector('.recording-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'recording-overlay';
        page.appendChild(overlay);
      }
    }
  });
}

/**
 * Remove all recording overlays (called when recording stops)
 */
function clearRecordingOverlays() {
  document.querySelectorAll('.recording-overlay').forEach((el) => el.remove());
  currentRecordingPageIndex = null;
}

/**
 * Show "Close Recorder" button below the book for adult recorders.
 * When clicked, closes the browser tab (since the admin app opens it in a new tab).
 */
function showCloseRecorderButton() {
  // Don't add duplicate buttons
  if (document.getElementById('closeRecorderBtn')) return;

  // Calculate vertical position: midpoint between bottom of book and bottom of viewport
  const flipBook = document.getElementById('flipBook');
  let bottomPos = '2rem'; // fallback
  if (flipBook) {
    const rect = flipBook.getBoundingClientRect();
    const bookBottom = rect.bottom;
    const viewportHeight = window.innerHeight;
    const midpoint = bookBottom + (viewportHeight - bookBottom) / 2;
    bottomPos = viewportHeight - midpoint + 'px';
  }

  const btn = document.createElement('button');
  btn.id = 'closeRecorderBtn';
  btn.className = 'close-recorder-btn';
  btn.style.bottom = bottomPos;
  btn.textContent = t('child.adultRecording.closeRecorder') || 'Sluit Opnemer';
  btn.addEventListener('click', () => {
    window.close();
    setTimeout(() => {
      btn.textContent = t('child.adultRecording.closeRecorder') || 'Sluit Opnemer';
      btn.disabled = true;
      btn.style.opacity = '0.5';
    }, 500);
  });

  document.body.appendChild(btn);
}

/**
 * Auto-advance to the next page after recording stops (adult recording mode).
 * Shows a progress indicator and flips to the next page after a short delay.
 * If all pages are recorded, shows a completion prompt.
 * Requirements: 11.4, 11.5, 22
 */
function autoAdvanceAfterRecording() {
  if (!pageFlipInstance || !currentStoryData) { recDbg.warn('autoAdvanceAfterRecording aborted — no pageFlipInstance or storyData'); return; }

  const currentFlipIndex = pageFlipInstance.getCurrentPageIndex();
  const totalFlipPages = pageFlipInstance.getPageCount();
  const totalStoryPages = currentStoryData?.pages?.length || 0;
  const recorded = recordedStoryPages();

  // Dump page recording statuses for diagnostic visibility
  const pageStatuses = {};
  pageRecordings.forEach((rec, idx) => {
    pageStatuses[`flip${idx}→p${rec.storyPage}`] = rec.status + (rec.url ? ' ✓url' : ' (no url)');
  });
  recDbg.trace('autoAdvanceAfterRecording', { currentFlipIndex, totalFlipPages, totalStoryPages, recordedCount: recorded.length, isAdult: isAdultNarrator(), pageStatuses });

  updatePageRecordedBadges();

  // Primary check: if all story pages are now recorded (or still being
  // finalized), finish regardless of where the flipbook currently sits.
  // iOS Safari has a bug where PageFlip wraps back to page 0 after the back
  // cover, making the position-based check below unreliable.
  // Count 'in-progress' pages too — a recording that's actively being saved
  // WILL complete, and the 200ms delay between stop and this function means
  // the onstop handler may not have promoted it to 'saving' yet.
  const allStatuses = ['ready', 'saving', 'in-progress'];
  let coveredPages = 0;
  pageRecordings.forEach((rec) => {
    if (rec && allStatuses.includes(rec.status) && typeof rec.storyPage === 'number') {
      coveredPages++;
    }
  });
  if (totalStoryPages > 0 && coveredPages >= totalStoryPages) {
    recDbg.ok(`All ${totalStoryPages} story pages covered (${coveredPages} with status ready/saving/in-progress) → calling sendRecordingAndFinish()`);
    sendRecordingAndFinish();
    return;
  }

  // Secondary check: position-based last-page detection (works on desktop/Android
  // where the flipbook doesn't wrap). Kept as a safety net.
  const isLastPage = currentFlipIndex >= totalFlipPages - 2;
  recDbg.log(`Last-page check: flipIndex=${currentFlipIndex}, total=${totalFlipPages}, threshold=${totalFlipPages - 2}, isLastPage=${isLastPage}`);
  if (isLastPage) {
    recDbg.ok('Last page detected (position) → calling sendRecordingAndFinish()');
    sendRecordingAndFinish();
    return;
  }

  // In double-page mode, check if the other page on this spread still needs recording
  if (isDoublePageMode()) {
    const isDone = (idx) => ['ready', 'saving'].includes(pageRecordings.get(idx)?.status);

    // If one page on this spread is still unrecorded, don't advance
    if (!isDone(currentFlipIndex) || !isDone(currentFlipIndex + 1)) {
      console.log('Staying on current spread - other page still needs recording');
      setCoachState('ready');
      return;
    }
  }

  // Auto-advance to next page after 1.5 seconds
  setTimeout(() => {
    if (pageFlipInstance) {
      const advanceBy = isDoublePageMode() ? 2 : 1;
      const nextPage = currentFlipIndex + advanceBy;
      if (nextPage < totalFlipPages) {
        setTimeout(() => {
          pageFlipInstance.flip(nextPage);
          console.log(
            'Auto-advanced to page',
            nextPage - PREPENDED_PAGE_COUNT + 1,
            'after recording'
          );
        }, 600);
      }
    }
  }, 1500);
}

/**
 * Show/update the recording progress indicator in adult recording mode.
 * Displays "Page X of Y recorded" below the audio controls.
 */
// Progress is now shown inside the coach toast (see progressLabel), so it can
// no longer push the player up the way the old in-flow indicator did.

/**
 * Show recording completion celebration when all pages are recorded.
 * Plays a reward sound and shows a warm message for the adult.
 */
function showRecordingComplete() {
  // One sound, owned here. playStarReward() plays its own copy of wow.mp3, so
  // the two must never both fire — hence the dedicated burst below.
  try {
    const celebrationSound = new Audio('/audio/wow.mp3');
    celebrationSound.volume = 1;
    celebrationSound.play().catch(() => { /* ignore autoplay restrictions */ });
  } catch { /* ignore audio errors */ }

  // Stars burst from the book and fade in place. The child animation flies them
  // to the achievements trophy, which is meaningless for adults (#126).
  playAdultCelebrationBurst();

  // No coach message here: sendRecordingAndFinish opens the completion toast
  // just after this, and that is the single messaging surface for finishing.
  // Showing a coach line first would flash a message and then hide it.
}

/**
 * Star burst centred on the book, fading in place.
 * Deliberately does not reuse playStarReward(), which targets the achievements
 * nav icon and plays its own copy of wow.mp3.
 *
 * @returns {void}
 */
function playAdultCelebrationBurst() {
  const anchor = document.querySelector('.story-content') || document.getElementById('flipBook');
  if (!anchor) return;

  const rect = anchor.getBoundingClientRect();
  const originX = rect.left + rect.width / 2;
  const originY = rect.top + rect.height / 2;

  const container = document.createElement('div');
  container.className = 'adult-celebration-burst';
  document.body.appendChild(container);

  const STAR_COUNT = 12;
  for (let i = 0; i < STAR_COUNT; i++) {
    const star = document.createElement('img');
    star.src = '/svg/components/YellowStar.svg';
    star.alt = '';
    star.setAttribute('aria-hidden', 'true');
    star.className = 'adult-celebration-burst__star';

    const angle = (i / STAR_COUNT) * Math.PI * 2;
    const distance = 90 + Math.random() * 70;

    star.style.left = `${originX}px`;
    star.style.top = `${originY}px`;
    star.style.setProperty('--burst-x', `${Math.cos(angle) * distance}px`);
    star.style.setProperty('--burst-y', `${Math.sin(angle) * distance}px`);
    star.style.animationDelay = `${Math.random() * 120}ms`;

    container.appendChild(star);
  }

  setTimeout(() => container.remove(), 1800);
}

async function toggleRecording() {
  recDbg.trace('toggleRecording', { isRecording, currentRecordingPageIndex, currentReadingMode });

  // Block recording on pre-story pages (cover, index/glossary)
  if (!isRecording && pageFlipInstance) {
    const currentPage = pageFlipInstance.getCurrentPageIndex();
    if (currentPage < PREPENDED_PAGE_COUNT) {
      recDbg.warn('toggleRecording blocked — on pre-story page', { currentPage, PREPENDED_PAGE_COUNT });
      return;
    }
  }

  const recordIcon = document.getElementById('recordIcon');
  const stopIcon = document.getElementById('stopIcon');
  const stopActiveIcon = document.getElementById('stopActiveIcon');
  const recordBtn = document.getElementById('recordBtn');
  const playPauseBtn = document.getElementById('playPauseBtn');

  if (!recordIcon || !stopIcon || !stopActiveIcon) return;

  if (isRecording) {
    // === STOP RECORDING ===
    isRecording = false;
    stopRecordFlash();
    clearTimeout(recordingAutoSaveTimeout);
    stopSilenceDetection();

    // Stop the MediaRecorder — 'onstop' handler will create the blob URL
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    // Stop microphone stream
    if (micStream) {
      micStream.getTracks().forEach((track) => track.stop());
      micStream = null;
    }

    recordIcon.classList.remove('audio-icon--hidden');
    stopIcon.classList.add('audio-icon--hidden');
    stopActiveIcon.classList.add('audio-icon--hidden');
    recordBtn.setAttribute('aria-label', 'Record audio');

    // Confirm the save on the user's tap, not when the async upload finishes
    markPageRecordedOptimistic(currentRecordingPageIndex);

    // Re-enable play button
    if (playPauseBtn) playPauseBtn.disabled = false;

    // Flash star indicator on recording save (Req 5.5) — no chime during recording
    bounceStarButton(false);

    // Remove recording page overlays
    // Remember which page was just recorded so next record press advances to the other page
    lastRecordedPageIndex = currentRecordingPageIndex;
    clearRecordingOverlays();

    // Auto-advance to next page after recording (Req 11.4)
    // Delayed slightly to allow mediaRecorder.onstop to save the recording first
    if (pageFlipInstance) {
      recDbg.ok('Recording stopped — scheduling autoAdvanceAfterRecording (200ms delay)', { lastRecordedPageIndex });
      setTimeout(() => autoAdvanceAfterRecording(), 200);
    }

    console.log('⏹️ Recording stopped');
  } else {
    // === START RECORDING ===
    // Reuse the existing mic stream if it is still active (kept alive between
    // pages by stopRecordingForPageTurn). This skips getUserMedia (~300-800ms)
    // and eliminates the silence at the start of subsequent pages (#155).
    if (!micStream || !micStream.active) {
      try {
        const { checkMicPermission } = await import('../utils/micPermission.js');
        micStream = await checkMicPermission();
      } catch (err) {
        console.error('🎙️ Microphone permission denied or unavailable:', err);
        return;
      }
    }

    // Show 3-2-1 countdown before recording starts
    // Full countdown on first recording of the session; short pulse on subsequent pages
    try {
      const { showRecordingCountdown, showRecordingReadyPulse } = await import('../utils/recordingCountdown.js');
      if (sessionRecordCount === 0) {
        await showRecordingCountdown();
      } else {
        await showRecordingReadyPulse();
      }
    } catch (err) {
      console.error('Countdown error:', err);
    }

    // Recording is live — tell the narrator how to turn the page or pause
    setCoachState('recording');

    // Revoke previous blob URL for this page to free memory
    // (per-page revocation happens after we determine currentRecordingPageIndex below)

    recordedChunks = [];
    lastRecordingStatus = 'in-progress';

    // Choose a supported MIME type
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';

    mediaRecorder = new MediaRecorder(micStream, { mimeType });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        recordedChunks.push(e.data);
      }
    };

    // Capture the page index in closure so onstop saves to the correct page
    const recordingPageIdx = (() => {
      if (pageFlipInstance) {
        const leftPage = pageFlipInstance.getCurrentPageIndex();
        // In double-page mode, if left page is a prepended page (cover/glossary),
        // record the right page (first story page) instead
        if (leftPage < PREPENDED_PAGE_COUNT) {
          return PREPENDED_PAGE_COUNT;
        }
        if (lastRecordedPageIndex === leftPage) return leftPage + 1;
        return leftPage;
      }
      return null;
    })();

    mediaRecorder.onstop = async () => {
      const rawBlob = new Blob(recordedChunks, { type: mimeType });

      // ── Normalize audio before saving/uploading ──
      let blob = rawBlob;
      let tooQuiet = false;
      try {
        const { normalizeBlob } = await import('../utils/audioNormalizer.js');
        const result = await normalizeBlob(rawBlob);
        if (result.tooQuiet) {
          tooQuiet = true;
          console.warn('⚠️ Recording too quiet — skipping upload');
        } else if (result.blob) {
          blob = result.blob;
        }
      } catch (normErr) {
        console.warn('⚠️ Normalization failed, using raw recording:', normErr.message);
      }

      if (tooQuiet) {
        lastRecordingStatus = 'too-quiet';

        // The page was marked recorded on the tap; undo that and explain via
        // the coach, which both profiles now share.
        revertOptimisticMark(
          recordingPageIdx,
          t('child.recording.tooQuiet') || 'Opname te stil — probeer nader aan die mikrofoon beweeg'
        );
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      lastRecordingUrl = blobUrl;
      lastRecordingStatus = 'ready';

      // Calculate duration from timer (fallback) and try to get actual from audio
      const timerDuration = recordingStartTime
        ? Math.round((Date.now() - recordingStartTime) / 1000)
        : 0;

      // Save to per-page map
      if (recordingPageIdx !== null) {
        // Revoke old blob for this page if it exists
        const prev = pageRecordings.get(recordingPageIdx);
        if (prev && prev.url) URL.revokeObjectURL(prev.url);

        const storyPage = recordingPageIdx - PREPENDED_PAGE_COUNT + 1;
        pageRecordings.set(recordingPageIdx, { url: blobUrl, status: 'ready', storyPage });
        console.log(
          '🎵 Recording saved for page',
          storyPage,
          '(index',
          recordingPageIdx + '):',
          blobUrl,
          'size:',
          blob.size,
          'bytes'
        );

        // Both readers were already confirmed on their tap by
        // markPageRecordedOptimistic — the coach for adults, a toast for
        // children. Confirming again here would arrive after normalisation,
        // which is too late to be useful.

        // Get actual audio duration from blob, then upload
        if (currentProfile && currentModule && !currentProfile.isGuest) {
          const isAdultMode = currentProfile.isAdultRecorder && adultRecordingParams;
          const isAdultProfile = currentProfile.type === 'adult' || isAdultMode;

          // Try to get real duration from audio element
          const tempAudio = new Audio(blobUrl);
          // loadedmetadata, error and the 3s fallback can race — only upload once
          let uploadStarted = false;
          const uploadWithDuration = async (duration) => {
            if (uploadStarted) return;
            uploadStarted = true;

            const finalDuration = Math.round(duration) || timerDuration;
            console.log(`🎵 Recording duration: ${finalDuration}s (timer: ${timerDuration}s)`);

            const familyId =
              (isAdultMode ? adultRecordingParams.familyId : null) || getFamilyId();
            const metadata = {
              profileId: currentProfile.id,
              familyId,
              moduleId: currentModule.id,
              pageNumber: storyPage,
              recordingType: isAdultProfile ? 'family_narration' : 'child',
              displayName: isAdultMode
                ? adultRecordingParams.displayName
                : (currentProfile.name || currentProfile.firstName || undefined),
              durationSeconds: finalDuration,
            };

            // Persist locally BEFORE attempting the upload, so a closed tab or a
            // dropped connection cannot destroy the recording (#150). The local
            // copy is removed only once the server confirms.
            const key = await savePendingRecording({ ...metadata, blob });

            try {
              await uploadRecording(blob, metadata);
              console.log(`✅ Recording uploaded: page ${storyPage}`);
              if (key) await deletePendingRecording(key);

              // Mark module as having a family recording in cached data
              if (isAdultProfile) {
                try {
                  const cached = JSON.parse(localStorage.getItem(`kassie_module_${currentModule.id}`) || '{}');
                  cached.hasFamilyRecording = true;
                  localStorage.setItem(`kassie_module_${currentModule.id}`, JSON.stringify(cached));
                } catch { /* ignore */ }
              }
            } catch (err) {
              // Recording stays in IndexedDB and is retried on reconnect / next open
              console.warn(
                `⚠️ Upload failed for page ${storyPage} — kept on device, queued for retry:`,
                err.message
              );
            }
          };

          tempAudio.addEventListener('loadedmetadata', () => {
            if (tempAudio.duration && isFinite(tempAudio.duration)) {
              uploadWithDuration(tempAudio.duration);
            } else {
              uploadWithDuration(timerDuration);
            }
          });
          tempAudio.addEventListener('error', () => {
            uploadWithDuration(timerDuration);
          });
          // Fallback if loadedmetadata doesn't fire within 3s
          setTimeout(() => {
            if (!tempAudio.duration || !isFinite(tempAudio.duration)) {
              uploadWithDuration(timerDuration);
            }
          }, 3000);
        }
      } else {
        console.log('🎵 Recording blob created:', blobUrl, 'size:', blob.size, 'bytes');
      }
    };

    mediaRecorder.start(100); // Collect data every 100ms

    isRecording = true;
    sessionRecordCount++;
    recordingStartTime = Date.now();

    // Start silence detection (calibrates voice level, then monitors for silence)
    startSilenceDetection();
    recordIcon.classList.add('audio-icon--hidden');
    stopIcon.classList.remove('audio-icon--hidden');
    stopActiveIcon.classList.add('audio-icon--hidden');
    recordBtn.setAttribute('aria-label', 'Stop recording');

    // Disable play button during recording
    if (playPauseBtn) playPauseBtn.disabled = true;

    // Start 500ms flashing between StopButton and StopButtonActive
    startRecordFlash();

    // Track which page is being recorded and show overlay on the other page
    currentRecordingPageIndex = recordingPageIdx;
    if (recordingPageIdx !== null) {
      // Mark page as in-progress in the map
      const storyPage = recordingPageIdx - PREPENDED_PAGE_COUNT + 1;
      pageRecordings.set(recordingPageIdx, {
        ...(pageRecordings.get(recordingPageIdx) || {}),
        status: 'in-progress',
        storyPage,
      });
      // Clear the memory so it doesn't carry over to a different spread
      lastRecordedPageIndex = null;
    }
    updateRecordingOverlays();

    console.log('🔴 Recording started (mic active), page index:', currentRecordingPageIndex);
  }
}

function startRecordFlash() {
  const stopIcon = document.getElementById('stopIcon');
  const stopActiveIcon = document.getElementById('stopActiveIcon');
  let showActive = false;

  recordFlashInterval = setInterval(() => {
    showActive = !showActive;
    if (showActive) {
      stopIcon.classList.add('audio-icon--hidden');
      stopActiveIcon.classList.remove('audio-icon--hidden');
    } else {
      stopIcon.classList.remove('audio-icon--hidden');
      stopActiveIcon.classList.add('audio-icon--hidden');
    }
  }, 500);
}

function stopRecordFlash() {
  if (recordFlashInterval) {
    clearInterval(recordFlashInterval);
    recordFlashInterval = null;
  }
}

/**
 * Stop recording silently for page turn (Req 6.1).
 * Saves the current page recording but doesn't flash star or re-enable play
 * because we're about to auto-start recording on the next page.
 * Returns a promise that resolves when the MediaRecorder has fully stopped.
 */
function stopRecordingForPageTurn() {
  return new Promise((resolve) => {
    if (!isRecording) {
      resolve();
      return;
    }

    isRecording = false;
    stopRecordFlash();
    clearTimeout(recordingAutoSaveTimeout);
    stopSilenceDetection();

    const recordIcon = document.getElementById('recordIcon');
    const stopIcon = document.getElementById('stopIcon');
    const stopActiveIcon = document.getElementById('stopActiveIcon');

    // Reset button icons
    if (recordIcon) recordIcon.classList.remove('audio-icon--hidden');
    if (stopIcon) stopIcon.classList.add('audio-icon--hidden');
    if (stopActiveIcon) stopActiveIcon.classList.add('audio-icon--hidden');

    // Remember which page was recorded
    lastRecordedPageIndex = currentRecordingPageIndex;
    clearRecordingOverlays();

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      // Wait for onstop to fire (blob is saved there)
      const origOnStop = mediaRecorder.onstop;
      mediaRecorder.onstop = (e) => {
        if (origOnStop) origOnStop(e);
        resolve();
      };
      mediaRecorder.stop();
    } else {
      resolve();
    }

    // Keep the mic stream alive so the next page does not need getUserMedia
    // again (~300-800ms latency that caused silence at the start of each page).
    // The stream is released on full stop or cleanup, not between pages.

    console.log('⏹️ Recording stopped for page turn (silent save, mic kept alive)');
  });
}

/**
 * Start silence detection using Web Audio API AnalyserNode.
 * Calibrates average voice level for first 2 seconds, then monitors for silence.
 */
function startSilenceDetection() {
  if (!micStream) return;

  try {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(micStream);
    analyserNode = audioContext.createAnalyser();
    analyserNode.fftSize = 2048;
    source.connect(analyserNode);

    voiceLevelSamples = [];
    averageVoiceLevel = 0;
    calibrationComplete = false;
    silenceStartTime = null;
    silenceConfirmationShown = false;

    const dataArray = new Uint8Array(analyserNode.fftSize);
    const calibrationStart = Date.now();

    function monitorLevels() {
      if (!isRecording) return;

      analyserNode.getByteTimeDomainData(dataArray);

      // Calculate RMS level (0-1 range)
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      // Calibration phase: collect voice level samples
      if (!calibrationComplete) {
        if (rms > 0.01) {
          voiceLevelSamples.push(rms);
        }
        if (Date.now() - calibrationStart > CALIBRATION_DURATION) {
          calibrationComplete = true;
          if (voiceLevelSamples.length > 0) {
            averageVoiceLevel =
              voiceLevelSamples.reduce((a, b) => a + b, 0) / voiceLevelSamples.length;
          } else {
            averageVoiceLevel = 0.02;
          }
          console.log('🎙️ Voice calibrated. Average level:', averageVoiceLevel.toFixed(4));
        }
        silenceDetectionRAF = requestAnimationFrame(monitorLevels);
        return;
      }

      // Detection phase: check for silence
      const silenceThreshold = averageVoiceLevel * SILENCE_THRESHOLD_RATIO;
      const isSilent = rms < silenceThreshold;

      if (isSilent) {
        if (!silenceStartTime) {
          silenceStartTime = Date.now();
        }
        const silenceDuration = Date.now() - silenceStartTime;

        if (silenceDuration >= SILENCE_DURATION_MS && !silenceConfirmationShown) {
          showSilenceConfirmation();
        }
      } else {
        silenceStartTime = null;
        if (silenceConfirmationShown) {
          dismissSilenceConfirmation();
        }
      }

      silenceDetectionRAF = requestAnimationFrame(monitorLevels);
    }

    silenceDetectionRAF = requestAnimationFrame(monitorLevels);
  } catch (err) {
    console.warn('⚠️ Silence detection unavailable:', err.message);
  }
}

function stopSilenceDetection() {
  if (silenceDetectionRAF) {
    cancelAnimationFrame(silenceDetectionRAF);
    silenceDetectionRAF = null;
  }
  if (silenceAutoStopTimeout) {
    clearTimeout(silenceAutoStopTimeout);
    silenceAutoStopTimeout = null;
  }
  // Remove any silence confirmation modal from DOM
  const modal = document.getElementById('silenceConfirmModal');
  if (modal) modal.remove();
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => { });
  }
  audioContext = null;
  analyserNode = null;
  dismissSilenceConfirmation();
}

function showSilenceConfirmation() {
  silenceConfirmationShown = true;

  if (navigator.vibrate) {
    navigator.vibrate([200, 100, 200]);
  }

  let modal = document.getElementById('silenceConfirmModal');
  if (!modal) {
    // Inject keyframe animation if not already present
    if (!document.getElementById('silencePulseStyle')) {
      const style = document.createElement('style');
      style.id = 'silencePulseStyle';
      style.textContent =
        '@keyframes silencePulse { 0%, 100% { border-color: red; background: rgba(0,0,0,0.85); } 50% { border-color: transparent; background: #d40000; } } @keyframes silenceBtnPulse { 0%, 100% { background: #44c1c5; } 50% { background: #d40000; } }';
      document.head.appendChild(style);
    }

    modal = document.createElement('div');
    modal.id = 'silenceConfirmModal';
    modal.style.cssText =
      'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:white;padding:12px 20px;border-radius:12px;z-index:9999;display:flex;align-items:center;gap:12px;font-size:0.9rem;box-shadow:0 4px 12px rgba(0,0,0,0.3);border:2px solid red;animation:silencePulse 1s ease-in-out infinite;';
    modal.innerHTML =
      '<span>🎙️ Neem jy nog op?</span><button id="silenceConfirmBtn" style="background:#44c1c5;color:white;border:none;padding:6px 14px;border-radius:8px;font-weight:600;cursor:pointer;animation:silenceBtnPulse 1s ease-in-out infinite;">Ja, ek is hier</button>';
    document.body.appendChild(modal);

    document.getElementById('silenceConfirmBtn').addEventListener('click', () => {
      dismissSilenceConfirmation();
      silenceStartTime = null;
    });
  }
  modal.style.display = 'flex';

  silenceAutoStopTimeout = setTimeout(() => {
    if (isRecording && silenceConfirmationShown) {
      console.log('⏱️ No response to silence prompt — auto-stopping recording');
      toggleRecording();
    }
  }, AUTO_STOP_AFTER_PROMPT_MS);
}

function dismissSilenceConfirmation() {
  silenceConfirmationShown = false;
  if (silenceAutoStopTimeout) {
    clearTimeout(silenceAutoStopTimeout);
    silenceAutoStopTimeout = null;
  }
  const modal = document.getElementById('silenceConfirmModal');
  if (modal) {
    modal.style.display = 'none';
  }
}

// Debug: press 'M' key to toggle silence confirmation modal
document.addEventListener('keydown', (e) => {
  if (e.key === 'm' || e.key === 'M') {
    if (silenceConfirmationShown) {
      dismissSilenceConfirmation();
    } else {
      showSilenceConfirmation();
    }
  }
});

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function onAudioEnded() {
  console.log('🎵 Audio ended on page');

  // Cancel progress tracking
  if (audioProgressRAF) {
    cancelAnimationFrame(audioProgressRAF);
    audioProgressRAF = null;
  }

  // Reset play button to PlayButton.svg
  const playIcon = document.getElementById('playIcon');
  const pauseIcon = document.getElementById('pauseIcon');
  if (playIcon && pauseIcon) {
    playIcon.classList.remove('audio-icon--hidden');
    pauseIcon.classList.add('audio-icon--hidden');
  }

  isAudioPlaying = false;

  // Re-enable record button after playback ends (only in record mode)
  const recordBtn = document.getElementById('recordBtn');
  if (recordBtn) recordBtn.disabled = currentReadingMode !== 'record';

  // In double-page spread mode, check if the other page
  // of the spread also has audio that needs to play before signalling page turn.
  if (isDoublePageMode() && currentReadingMode && currentReadingMode !== 'read') {
    const currentFlipIndex = pageFlipInstance ? pageFlipInstance.getCurrentPageIndex() : 0;
    const rightFlipIndex = currentFlipIndex + 1;

    // Determine which page just finished playing
    const justPlayedFlipIndex = audioElement?._kassieFlipIndex;

    // For record mode, check child recordings on the right page
    if (currentReadingMode === 'record') {
      const rightRec = pageRecordings.get(rightFlipIndex);
      if (
        justPlayedFlipIndex === currentFlipIndex &&
        rightRec &&
        rightRec.status === 'ready' &&
        rightRec.url
      ) {
        console.log('🔊 Left page recording ended, playing right page recording');
        const playIcon = document.getElementById('playIcon');
        const pauseIcon = document.getElementById('pauseIcon');
        const playPauseBtn = document.getElementById('playPauseBtn');
        if (playIcon && pauseIcon && playPauseBtn) {
          audioElement = new Audio(rightRec.url);
          audioElement._kassieFlipIndex = rightFlipIndex;
          playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
          return;
        }
      }
    } else if (currentReadingMode === 'listen-adult') {
      // Listen-adult: check adult recording on right page
      const rightStoryIdx = getStoryPageIndex(rightFlipIndex);
      const rightAdultUrl = getAdultRecordingUrl(rightStoryIdx + 1);

      if (justPlayedFlipIndex === currentFlipIndex && rightAdultUrl) {
        console.log(
          '🔊 Left page adult recording ended, playing right page adult recording (page',
          rightStoryIdx + 1,
          ')'
        );
        const playIcon2 = document.getElementById('playIcon');
        const pauseIcon2 = document.getElementById('pauseIcon');
        const playPauseBtn2 = document.getElementById('playPauseBtn');
        if (playIcon2 && pauseIcon2 && playPauseBtn2) {
          audioElement = new Audio(rightAdultUrl);
          audioElement._kassieFlipIndex = rightFlipIndex;
          playRealAudio(audioElement, playIcon2, pauseIcon2, playPauseBtn2);
          return;
        }
      }
    } else {
      // Listen / read-listen: check publisher audio on right page
      const rightStoryIdx = getStoryPageIndex(rightFlipIndex);
      const rightUrl = getPageAudioUrl(rightStoryIdx);

      if (justPlayedFlipIndex === currentFlipIndex && rightUrl) {
        console.log(
          '🔊 Left page audio ended, playing right page audio (page',
          rightStoryIdx + 1,
          ')'
        );
        playSpreadSecondPage(rightFlipIndex);
        return;
      }
    }
  }

  // All audio for this spread has played — signal page turn
  signalPageTurn();
}

/**
 * Play the second page's audio in a double-page spread.
 * Called when the first page's audio ends and the other page also has audio.
 */
function playSpreadSecondPage(flipbookPageIndex) {
  const playIcon = document.getElementById('playIcon');
  const pauseIcon = document.getElementById('pauseIcon');
  const playPauseBtn = document.getElementById('playPauseBtn');

  if (!playIcon || !pauseIcon || !playPauseBtn) return;

  const cached = pageAudioCache.get(flipbookPageIndex);
  if (cached && cached.status === 'ready') {
    audioElement = cached.audio;
    audioElement.currentTime = 0;
    audioElement._kassieFlipIndex = flipbookPageIndex;
    playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
  } else {
    // Load and play
    loadPageAudio(flipbookPageIndex).then((entry) => {
      if (entry && entry.status === 'ready') {
        audioElement = entry.audio;
        audioElement._kassieFlipIndex = flipbookPageIndex;
        playRealAudio(audioElement, playIcon, pauseIcon, playPauseBtn);
      } else {
        console.warn('⚠️ Could not load audio for spread second page');
        signalPageTurn();
      }
    });
  }
}

/**
 * Signal the child to turn the page (star bounce + page turn clip).
 * Extracted from onAudioEnded so it can be called after both spread pages finish.
 */
function signalPageTurn() {
  // Flash star indicator (Req 3.4) — no chime if recording is active
  bounceStarButton(currentReadingMode !== 'record');

  // Play Page_Turn_Clip then schedule auto-play of next page (Req 3.4, 3.5)
  playPageTurnClip().then(() => {
    // After page turn clip finishes, wait 2 seconds then auto-play next page
    scheduleAutoPlayNextPage();
  });
}

/**
 * Play the Page_Turn_Clip audio to signal the child to turn the page.
 * Returns a promise that resolves when the clip finishes (or immediately if unavailable).
 */
function playPageTurnClip() {
  return new Promise((resolve) => {
    ensureAudioUnlocked();

    if (!pageTurnClipAudio) {
      pageTurnClipAudio = new Audio(PAGE_TURN_CLIP_URL);
      pageTurnClipAudio.preload = 'auto';
    }

    pageTurnClipAudio.currentTime = 0;
    if (typeof window._kassieVolume === 'number') {
      pageTurnClipAudio.volume = window._kassieVolume;
    }
    pageTurnClipAudio.onended = () => resolve();
    pageTurnClipAudio.onerror = () => {
      console.warn('⚠️ Page turn clip unavailable, continuing without it');
      resolve();
    };

    pageTurnClipAudio.play().catch(() => {
      console.warn('⚠️ Page turn clip play() rejected');
      resolve();
    });
  });
}

/**
 * Schedule auto-play of the next page's audio after a 2-second delay.
 * Only applies in listen mode with publisher audio.
 */
function scheduleAutoPlayNextPage() {
  // The child should turn the page manually.
  // The star bounce + page turn clip (triggered in onAudioEnded) signal
  // that it's time to turn. The onFlip callback handles auto-playing
  // audio on the new page after the child turns.
  // No programmatic page flip here.
  console.log('🔊 Audio ended — waiting for child to turn the page');
}

/**
 * Chime sound played when star bounce starts (signals child to turn page).
 */
let chimeAudio = null;

function playChimeSound() {
  if (!chimeAudio) {
    chimeAudio = new Audio('/audio/chime-01.mp3');
    chimeAudio.preload = 'auto';
  }
  if (typeof window._kassieVolume === 'number') {
    chimeAudio.volume = window._kassieVolume;
  }
  chimeAudio.currentTime = 0;
  chimeAudio.play().catch(() => {
    // Silently ignore if play fails (e.g., no user interaction yet)
  });
}

/**
 * Animate the star button with a JS-driven bounce effect.
 * Bounces continuously until stopStarBounce() is called.
 */
let starBounceTimer = null;
let starBounceRunning = false;

function bounceStarButton(playSound = true) {
  const starBtn = document.getElementById('starBtn');
  if (!starBtn) return;

  // Cancel any running bounce
  stopStarBounce();

  starBounceRunning = true;
  if (playSound) {
    playChimeSound();
  }
  const duration = 500; // ms per cycle
  const maxScale = 1.8;
  const startTime = performance.now();

  // Rewind audio to start so replay begins from 0
  if (audioElement) {
    audioElement.currentTime = 0;
  }

  function step() {
    if (!starBounceRunning) {
      starBtn.style.transform = '';
      starBounceTimer = null;
      return;
    }

    const elapsed = performance.now() - startTime;
    const cycleProgress = (elapsed % duration) / duration;
    const t = Math.sin(cycleProgress * Math.PI);
    const scale = 1 + (maxScale - 1) * t;

    starBtn.style.transform = `scale(${scale})`;
    starBounceTimer = setTimeout(step, 16);
  }

  step();
}

function stopStarBounce() {
  starBounceRunning = false;
  if (starBounceTimer !== null) {
    clearTimeout(starBounceTimer);
    starBounceTimer = null;
  }
  const starBtn = document.getElementById('starBtn');
  if (starBtn) starBtn.style.transform = '';
}

/**
 * Clear any pending auto-play timer.
 */
function clearAutoPlayTimer() {
  if (autoPlayTimer) {
    clearTimeout(autoPlayTimer);
    autoPlayTimer = null;
  }
}

/**
 * Handle keyboard navigation
 */
function handleKeyboardNavigation(e) {
  if (!pageFlipInstance) return;

  // Toggle debug window with 'd' key
  if (e.key === 'd' || e.key === 'D') {
    toggleDebugWindow();
    e.preventDefault();
    return;
  }

  // Toggle recorder debug window with 'a' key
  if (e.key === 'a' || e.key === 'A') {
    toggleRecorderDebugWindow();
    e.preventDefault();
    return;
  }

  if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
    prevPage(pageFlipInstance);
    e.preventDefault();
  } else if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
    nextPage(pageFlipInstance);
    e.preventDefault();
  }
}

/**
 * Update page indicator
 */
function updatePageIndicator(pageIndex) {
  const pageCounter = document.getElementById('pageCounter');
  const progressFill = document.getElementById('progressFill');

  if (pageCounter && progressFill && pageFlipInstance) {
    const total = getPageCount(pageFlipInstance);
    const current = pageIndex + 1;

    pageCounter.textContent = `${current} / ${total}`;
    progressFill.style.width = `${(current / total) * 100}%`;
  }
}

/**
 * Track page view
 */
async function trackPageView(profile, module, pageIndex) {
  if (!profile || !module || !currentStoryData) return;

  // Skip tracking for guest/preview profiles
  if (profile.isGuest || profile.isPreview) {
    console.log('📊 Skipping progress tracking for preview/guest profile');
    return;
  }

  try {
    // Offset by PREPENDED_PAGE_COUNT (cover + index pages before story content)
    const storyPageIndex = pageIndex - PREPENDED_PAGE_COUNT;

    // Skip tracking for cover and index pages
    if (storyPageIndex < 0) return;

    const page = currentStoryData.pages[storyPageIndex];

    if (page) {
      await trackProgress(
        profile.id,
        module.id,
        `page-${page.pageNumber}`,
        'page_completed',
        5, // Approximate time spent
        { pageNumber: page.pageNumber }
      );
    }

    // Check if story is completed (last page)
    if (storyPageIndex === currentStoryData.pages.length - 1) {
      await trackProgress(profile.id, module.id, 'story-end', 'story_completed', 0, {
        title: module.title,
      });

      // Award stars for completion
      await awardStars(profile.id, 10, 'story_completed');
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

/**
 * Show fallback reader (non-animated)
 */
function showFallbackReader(profile, module, storyData) {
  console.log('Using fallback story reader (no animations)');
  // Fallback to simple page viewer if page flip fails
  const flipBook = document.getElementById('flipBook');
  if (flipBook) {
    flipBook.innerHTML = `
      <div style="background: white; padding: 2rem; border-radius: 8px; max-width: 600px;">
        <h2>${module.title}</h2>
        <p>Page flip animations unavailable. Displaying simple view.</p>
        <div id="simplePage" style="margin-top: 1rem;">
          ${storyData.pages[0] ? `<p>${storyData.pages[0].text}</p>` : ''}
        </div>
      </div>
    `;
  }
}

/**
 * Render content not ready state (module exists but content not uploaded yet)
 */
function renderContentNotReady(profile, module) {
  const contentContainer = document.getElementById('content-container');
  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  contentContainer.innerHTML = `
    <div class="error-screen child-friendly">
      <img src="/svg/branding/KassieAvatar.svg" alt="Kassie" width="120">
      <h1>${t('child.content.comingSoon') || 'Binnekort beskikbaar!'}</h1>
      <p>${t('child.content.contentNotReady') || 'Hierdie storie word nog gereed gemaak. Kom gou weer terug!'}</p>
      <p style="color: #666; font-size: 0.9em; margin-top: 1rem;">${module?.title || 'Module'}</p>

      <div class="error-actions">
        <button onclick="history.back()" class="custom-button">
          <div class="bottom-div cyanBG"></div>
          <div class="top-div kassieBG">
            <div class="content-wrapper">
              <img class="icon-placeholder" src="/svg/icons/LeftArrow.svg" alt="" width="20" height="20" />
              <span class="button-text">${t('child.actions.goBack')}</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  `;
}

/**
 * Render story error state
 */
function renderStoryError(profile, module, error) {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="error-screen child-friendly">
      <p><img src="/svg/branding/KassieAvatar.svg" alt="Kassie" width="120"></p>
      <h1>${t('child.errors.oops')}</h1>
      <h2>${t('child.errors.couldNotLoadStory')}</h2>
      <p>${error ? `<div class="error-message-box">${t('child.errors.storyRequiresInternet')}</div>` : ''}</p>
      <div class="error-actions">
        <button onclick="location.reload()" class="custom-button">
          <div class="bottom-div cyanBG"></div>
          <div class="top-div kassieBG">
            <img class="icon-placeholder" src="/svg/icons/Reload.svg" alt="" width="20" height="20" />
            <span class="button-text">${t('child.actions.tryAgain')}</span>
          </div>
        </button>
        <button onclick="history.back()" class="custom-button">
          <div class="bottom-div pinkBG"></div>
          <div class="top-div kassieBG">
            <div class="content-wrapper">
              <img class="icon-placeholder" src="/svg/icons/LeftArrow.svg" alt="" width="20" height="20" />
              <span class="button-text">${t('child.actions.goBack')}</span>
            </div>
          </div>
        </button>
      </div>
    </div>
  `;
}

/**
 * Debug Window Functions
 */

/**
 * Toggle debug window visibility
 */
function toggleDebugWindow() {
  const existingDebugWindow = document.getElementById('debugWindow');

  if (existingDebugWindow) {
    existingDebugWindow.remove();
  } else {
    createDebugWindow();
  }
}

/**
 * Create and display debug window with device breakpoint information
 */
function createDebugWindow() {
  // Import device detection and breakpoint functions
  const deviceInfo = detectDeviceAndOrientation();
  const pageFlipMode = getPageFlipMode(deviceInfo.deviceId, deviceInfo.orientation);

  // Get viewport dimensions
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Detect current page/view
  const currentView = detectCurrentView();

  // Get page-specific details
  const pageSpecificHTML = getPageSpecificDetails(currentView);

  // Get page-body dimensions if available
  const pageBody = document.querySelector('.page-body');
  const pageBodyWidth = pageBody ? pageBody.offsetWidth : 'N/A';
  const pageBodyHeight = pageBody ? pageBody.offsetHeight : 'N/A';

  // Get flip-book dimensions if available
  const flipBook = document.getElementById('flipBook');
  const flipBookWidth = flipBook ? flipBook.offsetWidth : 'N/A';
  const flipBookHeight = flipBook ? flipBook.offsetHeight : 'N/A';

  // Get page dimensions if available
  const page = document.querySelector('.page');
  const pageWidth = page ? page.offsetWidth : 'N/A';
  const pageHeight = page ? page.offsetHeight : 'N/A';

  // Create debug window HTML
  const debugWindow = document.createElement('div');
  debugWindow.id = 'debugWindow';
  debugWindow.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.95);
    color: #00ff00;
    padding: 20px;
    border-radius: 8px;
    border: 2px solid #00ff00;
    z-index: 10000;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    max-width: 90%;
    max-height: 90%;
    overflow-y: auto;
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
  `;

  debugWindow.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; border-bottom: 1px solid #00ff00; padding-bottom: 10px;">
      <h3 style="margin: 0; color: #00ff00;">📊 Debug Info - ${currentView.name}</h3>
      <button id="closeDebugBtn" style="background: #ff0000; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-family: 'Courier New', monospace;">✕ Close</button>
    </div>

    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
      <div>
        <h4 style="color: #ffff00; margin: 10px 0 5px 0;">🖥️ Device Detection</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">Device ID:</td><td style="color: #00ffff;">${deviceInfo.deviceId}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Category:</td><td style="color: #00ffff;">${deviceInfo.category}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Orientation:</td><td style="color: #00ffff;">${deviceInfo.orientation}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Breakpoint:</td><td style="color: #00ffff;">${deviceInfo.breakpoint || 'N/A'}</td></tr>
        </table>

        <h4 style="color: #ffff00; margin: 15px 0 5px 0;">📐 Viewport Dimensions</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">Width:</td><td style="color: #00ffff;">${viewportWidth}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Height:</td><td style="color: #00ffff;">${viewportHeight}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Aspect Ratio:</td><td style="color: #00ffff;">${(viewportWidth / viewportHeight).toFixed(3)}</td></tr>
        </table>

        ${currentView.type === 'story-reader'
      ? `
        <h4 style="color: #ffff00; margin: 15px 0 5px 0;">📖 PageFlip Configuration</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">Mode:</td><td style="color: #00ffff;">${pageFlipMode || 'N/A'}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Expected Ratio:</td><td style="color: #00ffff;">${pageFlipMode === 'double' ? '8:5 (1.6)' : '9:16 (0.5625)'}</td></tr>
        </table>

        <h4 style="color: #ffff00; margin: 15px 0 5px 0;">📦 Container Dimensions</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">FlipBook Width:</td><td style="color: #00ffff;">${flipBookWidth}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">FlipBook Height:</td><td style="color: #00ffff;">${flipBookHeight}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Page Width:</td><td style="color: #00ffff;">${pageWidth}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">Page Height:</td><td style="color: #00ffff;">${pageHeight}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">PageBody Width:</td><td style="color: #00ffff;">${pageBodyWidth}px</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">PageBody Height:</td><td style="color: #00ffff;">${pageBodyHeight}px</td></tr>
        </table>
        `
      : ''
    }

        <h4 style="color: #ffff00; margin: 15px 0 5px 0;">🎨 Data Attributes</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">data-device:</td><td style="color: #00ffff;">${document.documentElement.dataset.device || 'N/A'}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">data-breakpoint:</td><td style="color: #00ffff;">${document.documentElement.dataset.breakpoint || 'N/A'}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">data-orientation:</td><td style="color: #00ffff;">${document.documentElement.dataset.orientation || 'N/A'}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">data-category:</td><td style="color: #00ffff;">${document.documentElement.dataset.category || 'N/A'}</td></tr>
        </table>

        <h4 style="color: #ffff00; margin: 15px 0 5px 0;">🔧 Container Query Support</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 3px 10px 3px 0;">CQI Units:</td><td style="color: #00ffff;">${CSS.supports('width', '1cqi') ? '✅ Supported' : '❌ Not Supported'}</td></tr>
          <tr><td style="padding: 3px 10px 3px 0;">CQB Units:</td><td style="color: #00ffff;">${CSS.supports('width', '1cqb') ? '✅ Supported' : '❌ Not Supported'}</td></tr>
        </table>
      </div>

      <div>
        ${pageSpecificHTML}
      </div>
    </div>

    <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #00ff00; font-size: 10px; color: #888;">
      Press 'D' key to toggle this window | Data refreshed: ${new Date().toLocaleTimeString()}
    </div>
  `;

  document.body.appendChild(debugWindow);

  // Bind close button
  const closeBtn = document.getElementById('closeDebugBtn');
  if (closeBtn) {
    closeBtn.addEventListener('click', toggleDebugWindow);
  }

  // Close on click outside
  debugWindow.addEventListener('click', (e) => {
    if (e.target === debugWindow) {
      toggleDebugWindow();
    }
  });
}

/**
 * Detect current view/page
 */
function detectCurrentView() {
  // Check for story reader
  if (document.querySelector('.story-reader-container')) {
    return { type: 'story-reader', name: 'Story Reader' };
  }

  // Check for library grid
  if (document.querySelector('.library-screen')) {
    return { type: 'library', name: 'Content Library' };
  }

  // Check for profile selection
  if (document.querySelector('.profile-selection')) {
    return { type: 'profile', name: 'Profile Selection' };
  }

  // Check for category view
  if (document.querySelector('.category-view')) {
    return { type: 'category', name: 'Category View' };
  }

  return { type: 'unknown', name: 'Unknown View' };
}

/**
 * Get page-specific debug details
 */
function getPageSpecificDetails(currentView) {
  switch (currentView.type) {
    case 'library':
      return getLibraryDetails();
    case 'story-reader':
      return getStoryReaderDetails();
    case 'profile':
      return getProfileDetails();
    case 'category':
      return getCategoryDetails();
    default:
      return '<p style="color: #888;">No page-specific details available</p>';
  }
}

/**
 * Get library grid details
 */
function getLibraryDetails() {
  const grid = document.querySelector('.library-grid');
  const cards = document.querySelectorAll('.content-card');
  const firstCard = cards[0];

  if (!grid) {
    return '<p style="color: #888;">Library grid not found</p>';
  }

  const gridStyles = window.getComputedStyle(grid);

  return `
    <h4 style="color: #ffff00; margin: 10px 0 5px 0;">📚 Library Grid Details</h4>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 3px 10px 3px 0;">Grid Width:</td><td style="color: #00ffff;">${grid.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Grid Height:</td><td style="color: #00ffff;">${grid.offsetHeight}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Columns:</td><td style="color: #00ffff;">${gridStyles.gridTemplateColumns}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Gap:</td><td style="color: #00ffff;">${gridStyles.gap}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Total Cards:</td><td style="color: #00ffff;">${cards.length}</td></tr>
      ${firstCard
      ? `
      <tr><td style="padding: 3px 10px 3px 0;">Card Width:</td><td style="color: #00ffff;">${firstCard.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Card Height:</td><td style="color: #00ffff;">${firstCard.offsetHeight}px</td></tr>
      `
      : '<tr><td colspan="2" style="color: #888;">No cards found</td></tr>'
    }
    </table>
  `;
}

/**
 * Get story reader-specific details
 */
function getStoryReaderDetails() {
  const pageImage = document.querySelector('.page-image');
  const pageText = document.querySelector('.page-text');
  const pageFooter = document.querySelector('.page-footer');
  const audioControls = document.getElementById('audioControls');

  const pageImageStyles = pageImage ? window.getComputedStyle(pageImage) : null;
  const pageTextStyles = pageText ? window.getComputedStyle(pageText) : null;
  const pageFooterStyles = pageFooter ? window.getComputedStyle(pageFooter) : null;

  return `
    <h4 style="color: #ffff00; margin: 10px 0 5px 0;">📄 Page Elements</h4>
    <table style="width: 100%; border-collapse: collapse;">
      ${pageImage
      ? `
      <tr><td colspan="2" style="padding: 8px 0 3px 0; color: #ffff00; font-weight: bold;">Page Image:</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Width:</td><td style="color: #00ffff;">${pageImage.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Height:</td><td style="color: #00ffff;">${pageImage.offsetHeight}px (${pageImageStyles.height})</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Max Height:</td><td style="color: #00ffff;">${pageImageStyles.maxHeight}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Flex:</td><td style="color: #00ffff;">${pageImageStyles.flex}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Margin:</td><td style="color: #00ffff;">${pageImageStyles.margin}</td></tr>
      `
      : ''
    }

      ${pageText
      ? `
      <tr><td colspan="2" style="padding: 8px 0 3px 0; color: #ffff00; font-weight: bold;">Page Text:</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Width:</td><td style="color: #00ffff;">${pageText.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Height:</td><td style="color: #00ffff;">${pageText.offsetHeight}px (${pageTextStyles.height})</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Max Height:</td><td style="color: #00ffff;">${pageTextStyles.maxHeight}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Flex:</td><td style="color: #00ffff;">${pageTextStyles.flex}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Font Size:</td><td style="color: #00ffff;">${pageTextStyles.fontSize}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Line Height:</td><td style="color: #00ffff;">${pageTextStyles.lineHeight}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Margin:</td><td style="color: #00ffff;">${pageTextStyles.marginTop} (top)</td></tr>
      `
      : ''
    }

      ${pageFooter
      ? `
      <tr><td colspan="2" style="padding: 8px 0 3px 0; color: #ffff00; font-weight: bold;">Page Footer:</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Width:</td><td style="color: #00ffff;">${pageFooter.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Height:</td><td style="color: #00ffff;">${pageFooter.offsetHeight}px (${pageFooterStyles.height})</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Max Height:</td><td style="color: #00ffff;">${pageFooterStyles.maxHeight}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Flex:</td><td style="color: #00ffff;">${pageFooterStyles.flex}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Font Size:</td><td style="color: #00ffff;">${pageFooterStyles.fontSize}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Margin Top:</td><td style="color: #00ffff;">${pageFooterStyles.marginTop}</td></tr>
      `
      : ''
    }
    </table>

    ${audioControls
      ? `
    <h4 style="color: #ffff00; margin: 15px 0 5px 0;">🎵 Audio Controls</h4>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 3px 10px 3px 0;">Width:</td><td style="color: #00ffff;">${audioControls.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Height:</td><td style="color: #00ffff;">${audioControls.offsetHeight}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Bottom:</td><td style="color: #00ffff;">${window.getComputedStyle(audioControls).bottom}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Display:</td><td style="color: #00ffff;">${window.getComputedStyle(audioControls).display}</td></tr>
    </table>
    `
      : ''
    }
  `;
}

/**
 * Get profile selection-specific details
 */
function getProfileDetails() {
  const profileCards = document.querySelectorAll('.profile-card');
  const firstProfile = profileCards[0];

  if (!firstProfile) {
    return '<p style="color: #888;">No profile cards found</p>';
  }

  const profileStyles = window.getComputedStyle(firstProfile);

  return `
    <h4 style="color: #ffff00; margin: 10px 0 5px 0;">👤 Profile Cards</h4>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 3px 10px 3px 0;">Total Profiles:</td><td style="color: #00ffff;">${profileCards.length}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Card Width:</td><td style="color: #00ffff;">${firstProfile.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Card Height:</td><td style="color: #00ffff;">${firstProfile.offsetHeight}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Padding:</td><td style="color: #00ffff;">${profileStyles.padding}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Margin:</td><td style="color: #00ffff;">${profileStyles.margin}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Border Radius:</td><td style="color: #00ffff;">${profileStyles.borderRadius}</td></tr>
    </table>
  `;
}

/**
 * Get category view-specific details
 */
function getCategoryDetails() {
  const categoryCards = document.querySelectorAll('.category-card');
  const firstCategory = categoryCards[0];

  if (!firstCategory) {
    return '<p style="color: #888;">No category cards found</p>';
  }

  const categoryStyles = window.getComputedStyle(firstCategory);

  return `
    <h4 style="color: #ffff00; margin: 10px 0 5px 0;">📚 Category Cards</h4>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 3px 10px 3px 0;">Total Categories:</td><td style="color: #00ffff;">${categoryCards.length}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Card Width:</td><td style="color: #00ffff;">${firstCategory.offsetWidth}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Card Height:</td><td style="color: #00ffff;">${firstCategory.offsetHeight}px</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Padding:</td><td style="color: #00ffff;">${categoryStyles.padding}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Margin:</td><td style="color: #00ffff;">${categoryStyles.margin}</td></tr>
      <tr><td style="padding: 3px 10px 3px 0;">Border Radius:</td><td style="color: #00ffff;">${categoryStyles.borderRadius}</td></tr>
    </table>
  `;
}

/* ========================================
   RECORDER DEBUG WINDOW
   Toggle with 'A' key — shows audio control states in real-time
   ======================================== */

let recorderDebugInterval = null;

function toggleRecorderDebugWindow() {
  const existing = document.getElementById('recorderDebugWindow');
  if (existing) {
    if (recorderDebugInterval) {
      clearInterval(recorderDebugInterval);
      recorderDebugInterval = null;
    }
    existing.remove();
    return;
  }
  createRecorderDebugWindow();
  // Auto-refresh every 250ms for real-time state
  recorderDebugInterval = setInterval(refreshRecorderDebug, 250);
}

function createRecorderDebugWindow() {
  const panel = document.createElement('div');
  panel.id = 'recorderDebugWindow';
  panel.style.cssText = `
    position: fixed;
    bottom: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.92);
    color: #00ff00;
    padding: 14px 18px;
    border-radius: 8px;
    border: 2px solid #ff6600;
    z-index: 10001;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    min-width: 340px;
    max-width: 420px;
    max-height: 70vh;
    overflow-y: auto;
    box-shadow: 0 0 20px rgba(255, 102, 0, 0.4);
    pointer-events: auto;
  `;
  panel.innerHTML = getRecorderDebugHTML();
  document.body.appendChild(panel);
}

function refreshRecorderDebug() {
  const panel = document.getElementById('recorderDebugWindow');
  if (!panel) {
    if (recorderDebugInterval) {
      clearInterval(recorderDebugInterval);
      recorderDebugInterval = null;
    }
    return;
  }
  panel.innerHTML = getRecorderDebugHTML();
}

function getRecorderDebugHTML() {
  // 1) Avatar
  const avatarImg = document.getElementById('audioAvatar');
  const avatarSrc = avatarImg ? avatarImg.src : 'N/A';
  const avatarDesc = avatarImg ? describeAvatar(avatarImg.src) : 'Not found';

  // 2) Play button
  const playIcon = document.getElementById('playIcon');
  const pauseIcon = document.getElementById('pauseIcon');
  const playPauseBtn = document.getElementById('playPauseBtn');
  const playVisible = playIcon && !playIcon.classList.contains('audio-icon--hidden');
  const pauseVisible = pauseIcon && !pauseIcon.classList.contains('audio-icon--hidden');
  const playDisabled = playPauseBtn ? playPauseBtn.disabled : 'N/A';
  const playState = pauseVisible ? 'PLAYING (showing Pause)' : 'STOPPED (showing Play)';
  const playImgSrc = pauseVisible ? pauseIcon?.src || 'N/A' : playIcon?.src || 'N/A';

  // 3) Record button
  const recordIcon = document.getElementById('recordIcon');
  const stopIcon = document.getElementById('stopIcon');
  const stopActiveIcon = document.getElementById('stopActiveIcon');
  const recordBtn = document.getElementById('recordBtn');
  const recordVisible = recordIcon && !recordIcon.classList.contains('audio-icon--hidden');
  const stopVisible = stopIcon && !stopIcon.classList.contains('audio-icon--hidden');
  const stopActiveVisible =
    stopActiveIcon && !stopActiveIcon.classList.contains('audio-icon--hidden');
  const recordDisabled = recordBtn ? recordBtn.disabled : 'N/A';
  let recordState = 'IDLE (showing Record)';
  let recordImgSrc = recordIcon?.src || 'N/A';
  if (stopVisible || stopActiveVisible) {
    recordState = 'RECORDING (showing Stop)';
    recordImgSrc = stopActiveVisible ? stopActiveIcon?.src || 'N/A' : stopIcon?.src || 'N/A';
  }
  const flashActive = recordFlashInterval !== null;

  // 4) Progress bar
  const progressFill = document.getElementById('audioProgressFill');
  const progressWidth = progressFill ? progressFill.style.width : '0%';
  const progressBar = progressFill?.parentElement;
  const progressDisabled = progressBar
    ? window.getComputedStyle(progressBar).pointerEvents === 'none'
      ? 'Yes'
      : 'No'
    : 'N/A';

  // 5) Star
  const starBtn = document.getElementById('starBtn');
  const starBouncing = starBtn ? starBtn.classList.contains('bouncing') : false;
  const starFavorited = starBtn ? starBtn.classList.contains('favorited') : false;
  const starDisabled = starBtn ? starBtn.disabled : 'N/A';

  // 6) Per-page recordings for visible pages
  const visiblePageRecordings = [];
  if (pageFlipInstance) {
    const flipIdx = pageFlipInstance.getCurrentPageIndex();
    const flipBookEl = document.getElementById('flipBook');
    const wrapper = flipBookEl?.querySelector('.stf__wrapper');
    const isDouble = wrapper?.classList.contains('--landscape');
    const pagesToShow = isDouble ? [flipIdx, flipIdx + 1] : [flipIdx];

    pagesToShow.forEach((idx) => {
      const storyPage = idx - PREPENDED_PAGE_COUNT + 1;
      if (storyPage < 1) return; // skip cover/index
      const rec = pageRecordings.get(idx);
      visiblePageRecordings.push({
        storyPage,
        pageIdx: idx,
        status: rec ? rec.status : 'none',
        url: rec ? rec.url : null,
        isRecording: currentRecordingPageIndex === idx && isRecording,
      });
    });
  }

  // 7) Recording page overlay
  const recPageIdx = currentRecordingPageIndex;
  const overlayCount = document.querySelectorAll('.recording-overlay').length;

  // 8) General state
  const mode = currentReadingMode || 'none';
  const audioPlaying = isAudioPlaying;
  const recording = isRecording;

  // 9) Publisher audio for current page
  const curFlipIdx = pageFlipInstance ? pageFlipInstance.getCurrentPageIndex() : 0;
  const curStoryIdx = getStoryPageIndex(curFlipIdx);
  const curAudioUrl = getPageAudioUrl(curStoryIdx);
  const curAudioCache = pageAudioCache.get(curFlipIdx);
  const curAudioStatus = curAudioCache
    ? curAudioCache.status
    : curAudioUrl
      ? 'not loaded'
      : 'no url';

  // Shorten URLs for display
  const shortUrl = (url) => {
    if (!url || url === 'N/A') return 'N/A';
    const parts = url.split('/');
    return parts.slice(-2).join('/');
  };

  return `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; border-bottom: 1px solid #ff6600; padding-bottom: 6px;">
      <span style="color: #ff6600; font-weight: bold;">🎙️ Recorder Debug</span>
      <span style="color: #888; font-size: 9px;">${new Date().toLocaleTimeString()} | Press A to close</span>
    </div>

    <div style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px solid #333;">
      <span style="color: #ffff00;">⚙️ Mode:</span> <span style="color: #00ffff;">${mode}</span>
      &nbsp;|&nbsp;<span style="color: #ffff00;">Playing:</span> <span style="color: ${audioPlaying ? '#ff4444' : '#00ff00'};">${audioPlaying}</span>
      &nbsp;|&nbsp;<span style="color: #ffff00;">Recording:</span> <span style="color: ${recording ? '#ff4444' : '#00ff00'};">${recording}</span>
      &nbsp;|&nbsp;<span style="color: #ffff00;">AutoPlay:</span> <span style="color: ${autoPlayTimer ? '#ffff00' : '#888'};">${autoPlayTimer ? 'pending' : 'none'}</span>
    </div>

    <table style="width: 100%; border-collapse: collapse;">
      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 6px 0 3px 0; color: #ff6600; font-weight: bold;">1) Avatar</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">URL:</td><td style="color: #00ffff; word-break: break-all;">${shortUrl(avatarSrc)}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Description:</td><td style="color: #00ffff;">${avatarDesc}</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">2) Play Button</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">State:</td><td style="color: ${pauseVisible ? '#ff4444' : '#00ff00'};">${playState}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Visible img:</td><td style="color: #00ffff;">${shortUrl(playImgSrc)}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Disabled:</td><td style="color: ${playDisabled ? '#ff4444' : '#00ff00'};">${playDisabled}</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">3) Record Button</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">State:</td><td style="color: ${!recordVisible ? '#ff4444' : '#00ff00'};">${recordState}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Visible img:</td><td style="color: #00ffff;">${shortUrl(recordImgSrc)}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Disabled:</td><td style="color: ${recordDisabled ? '#ff4444' : '#00ff00'};">${recordDisabled}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Flash active:</td><td style="color: ${flashActive ? '#ff4444' : '#00ff00'};">${flashActive}</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">4) Progress Bar</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Fill:</td><td style="color: #00ffff;">${progressWidth}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Disabled:</td><td style="color: #00ffff;">${progressDisabled}</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">5) Star</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Bouncing:</td><td style="color: ${starBouncing ? '#ff4444' : '#00ff00'};">${starBouncing}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Favorited:</td><td style="color: ${starFavorited ? '#ffff00' : '#00ff00'};">${starFavorited}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Disabled:</td><td style="color: #00ffff;">${starDisabled}</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">6) Publisher Audio</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Page ${curStoryIdx + 1} URL:</td><td style="color: #00ffff; word-break: break-all;">${curAudioUrl ? shortUrl(curAudioUrl) : '<span style="color: #888;">none</span>'}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Status:</td><td style="color: ${curAudioStatus === 'ready' ? '#00ff00' : curAudioStatus === 'loading' ? '#ffff00' : curAudioStatus === 'error' ? '#ff4444' : '#888'};">${curAudioStatus}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Cache size:</td><td style="color: #00ffff;">${pageAudioCache.size} pages</td></tr>

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">7) Page Recordings</td>
      </tr>
      ${visiblePageRecordings.length === 0
      ? '<tr><td colspan="2" style="padding: 2px 8px; color: #888;">No story pages visible</td></tr>'
      : visiblePageRecordings
        .map(
          (pr) => `
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Page ${pr.storyPage}:</td><td>${pr.isRecording
              ? '<span style="color: #ff4444;">⏺ Recording now...</span>'
              : pr.status === 'ready' && pr.url
                ? `<a href="${pr.url}" target="_blank" style="color: #00ffff; text-decoration: underline;">▶ Play page ${pr.storyPage}</a>`
                : pr.status === 'in-progress'
                  ? '<span style="color: #ffff00;">⏺ Recording...</span>'
                  : '<span style="color: #888;">no recording</span>'
            }</td></tr>`
        )
        .join('')
    }

      <tr style="border-bottom: 1px solid #333;">
        <td colspan="2" style="padding: 8px 0 3px 0; color: #ff6600; font-weight: bold;">8) Overlay</td>
      </tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Rec page idx:</td><td style="color: #00ffff;">${recPageIdx !== null ? recPageIdx : 'none'}</td></tr>
      <tr><td style="padding: 2px 8px 2px 0; color: #888;">Overlays:</td><td style="color: ${overlayCount > 0 ? '#ffff00' : '#888'};">${overlayCount}</td></tr>
    </table>
  `;
}

function describeAvatar(src) {
  if (!src) return 'None';
  if (src.includes('KassieAvatar')) return 'Kassie (Publisher)';
  if (src.includes('GrownUp')) return 'Adult/GrownUp';
  const filename = src.split('/').pop();
  return filename || 'Unknown';
}
